<template>
  <div class="health-page">
    <section class="health-hero">
      <div class="label">
        <div>
          <span class="eyebrow">Catalog automation</span>
          <h4>Catalog Health</h4>
        </div>
        <button class="btn-refresh" @click="fetchAll()">Refresh</button>
      </div>
      <p class="intro">
        Standards, completeness and image coverage across the parts catalog. Run a scan to
        re-evaluate findings, fix the deterministic ones automatically, or send the hard cases
        to AI research. Long-running jobs report progress in
        <router-link class="inline-link" to="/users/jobs">Scheduled Jobs</router-link>.
      </p>
      <p v-if="qaScheduleLine" class="qa-schedule">
        <span class="qa-dot" :class="{ 'is-on': qaPipeline.enabled }" aria-hidden="true"></span>
        <span>{{ qaScheduleLine }}</span>
        <router-link class="inline-link" to="/users/pipelines">Schedule &amp; steps</router-link>
      </p>
    </section>

    <div v-if="error" class="alert">{{ error }}</div>
    <div v-if="notice" class="notice">{{ notice }}</div>
    <div v-if="trackedRun" class="job-progress-card" :class="{ 'is-running': isRunActive(trackedRun) }">
      <div class="job-progress-head">
        <div>
          <span class="eyebrow">{{ trackedJobLabel || prettyLabel(runJobKey(trackedRun)) }}</span>
          <h5>
            Run #{{ runId(trackedRun) || 'pending' }}
            <span class="run-status" :class="'st-' + runStatus(trackedRun)">{{ prettyLabel(runStatus(trackedRun)) }}</span>
          </h5>
        </div>
        <router-link class="inline-link" to="/users/jobs">Open full log</router-link>
      </div>
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: runProgress(trackedRun) + '%' }"></div>
      </div>
      <div class="job-progress-meta">
        <span>{{ runDuration(trackedRun) }}</span>
        <span v-if="isRunActive(trackedRun)">{{ liveConnected ? 'Live updates active' : 'Waiting for live updates' }}</span>
        <span v-else>Last updated {{ fmtRelative(runEndedAt(trackedRun) || runStartedAt(trackedRun)) }}</span>
      </div>
      <div v-if="runSummaryChips(trackedRun).length" class="job-progress-chips">
        <span v-for="chip in runSummaryChips(trackedRun)" :key="chip.key" class="job-chip" :class="'tone-' + chip.tone">
          <span>{{ chip.label }}</span>
          <strong>{{ chip.value }}</strong>
        </span>
      </div>
    </div>

    <!-- KPIs -->
    <div class="kpi-row">
      <div class="kpi-card">
        <span class="kpi-label">Total parts</span>
        <span class="kpi-value">{{ kpis.parts }}</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Open findings</span>
        <span class="kpi-value">{{ kpis.open }}</span>
      </div>
      <div class="kpi-card tone-bad">
        <span class="kpi-label">Blockers</span>
        <span class="kpi-value">{{ kpis.blockers }}</span>
      </div>
      <div class="kpi-card tone-warn">
        <span class="kpi-label">Warnings</span>
        <span class="kpi-value">{{ kpis.warnings }}</span>
      </div>
    </div>

    <!-- Actions -->
    <div class="actions-bar">
      <button class="gc-act primary" :disabled="!!runningJob" @click="runScan">
        {{ runningJob === 'catalog-health-scan' ? 'Scanning…' : 'Run scan' }}
      </button>
      <button class="gc-act" :disabled="!!runningJob" @click="dryRunFixes">
        {{ runningJob === 'auto-fix-part-standards-dry' ? 'Running…' : 'Dry-run fixes' }}
      </button>
      <button class="gc-act" :disabled="!!runningJob" @click="applyFixes">
        {{ runningJob === 'auto-fix-part-standards' ? 'Applying…' : 'Apply fixes' }}
      </button>
      <button class="gc-act" :disabled="!!runningJob" @click="aiResearch">
        {{ runningJob === 'ai-research-part-health-fixes' ? 'Researching…' : 'AI research hard findings' }}
      </button>
      <button class="gc-act" :disabled="!!runningJob" @click="repairImages">
        {{ runningJob === 'repair-missing-images' ? 'Repairing…' : 'Repair missing images' }}
      </button>
      <div class="amazon-price-run-control">
        <label>
          <span>Price items</span>
          <input
            v-model.number="amazonPriceLimit"
            type="number"
            min="1"
            step="1"
            :placeholder="amazonPriceEligibleCount === null
              ? String(amazonPriceFindingCount || '')
              : String(amazonPriceEligibleCount)"
            @input="amazonPriceLimitTouched = true"
            @blur="normalizeAmazonPriceLimit"
          >
        </label>
        <span class="amazon-price-scope-note">
          {{ amazonPriceScopeLoading
            ? 'Counting missing prices…'
            : (amazonPriceEligibleCount === null ? 'Count unavailable' : amazonPriceEligibleCount + ' missing') }}
        </span>
        <button
          class="gc-act amazon-price-action"
          :disabled="!!runningJob || !validAmazonPriceLimit"
          @click="refreshMissingAmazonPrices"
        >
          {{ runningJob === 'refresh-candidate-prices' ? 'Refreshing prices…' : 'Retry Amazon prices' }}
        </button>
      </div>
    </div>

    <!-- Category drilldown -->
    <div class="section-head">
      <span class="eyebrow">By part type</span>
      <h5>Category drilldown</h5>
    </div>
    <div class="cat-grid">
      <button
        v-for="cat in categories"
        :key="cat.type"
        class="cat-tile"
        :class="{ 'is-active': filters.type === cat.type }"
        @click="selectType(cat.type)"
      >
        <span class="cat-name">{{ prettyType(cat.type) }}</span>
        <span class="cat-total">{{ cat.total }} parts</span>
        <span class="cat-pills">
          <span class="mini-pill tone-bad" :class="{ dim: !cat.blockers }">{{ cat.blockers }} blk</span>
          <span class="mini-pill tone-warn" :class="{ dim: !cat.warnings }">{{ cat.warnings }} warn</span>
        </span>
      </button>
      <div v-if="!categories.length" class="cat-empty">No category breakdown yet — run a scan.</div>
    </div>

    <!-- Filters + tabs -->
    <div class="tabs-row">
      <div class="tabs">
        <button class="tab" :class="{ active: activeTab === 'findings' }" @click="activeTab = 'findings'">
          Findings <span class="tab-count">{{ visibleFindings.length }}</span>
        </button>
        <button class="tab" :class="{ active: activeTab === 'parts' }" @click="activeTab = 'parts'">
          Affected parts <span class="tab-count">{{ parts.length }}</span>
        </button>
        <button class="tab" :class="{ active: activeTab === 'audits' }" @click="activeTab = 'audits'">
          Audit history <span class="tab-count">{{ audits.length }}</span>
        </button>
      </div>
      <div class="filters" v-if="activeTab !== 'audits'">
        <select class="hfilter" v-model="filters.catalogScope" @change="onFilterChange">
          <option
            v-for="scope in catalogScopeOptions"
            :key="scope.value"
            :value="scope.value"
          >{{ scope.label }}</option>
        </select>
        <select class="hfilter" v-model="filters.type" @change="onFilterChange">
          <option v-for="t in partTypes" :key="t" :value="t">{{ t === 'all' ? 'All types' : prettyType(t) }}</option>
        </select>
        <select class="hfilter" v-model="filters.severity" @change="onFilterChange">
          <option value="all">Any severity</option>
          <option value="blocker">Blockers</option>
          <option value="warning">Warnings</option>
        </select>
        <select class="hfilter" v-model="filters.issue" @change="onFilterChange">
          <option value="all">All issues</option>
          <option v-for="i in issueOptions" :key="i" :value="i">{{ prettyLabel(i) }}</option>
        </select>
        <select class="hfilter" v-model="filters.status" @change="onFilterChange">
          <option value="open">Open</option>
          <option value="ignored">Ignored</option>
          <option value="resolved">Resolved</option>
          <option value="all">Any status</option>
        </select>
      </div>
    </div>

    <div v-if="focusPart" class="focus-banner">
      Showing findings for <strong>{{ focusPartName }}</strong>
      <a class="focus-clear" @click="clearFocus">clear ×</a>
    </div>

    <!-- Findings -->
    <div v-show="activeTab === 'findings'" class="catalog-table-section">
      <AdminTable
        class="health-admin-table"
        :columns="findingColumns"
        :rows="visibleFindings"
        :row-key="fid"
        min-width="1120px"
        empty-text="No findings for these filters."
        clickable
        @row-click="openFindingDetail"
      >
        <template v-slot:cell-category="{ row }">
          <div class="finding-classification">
            <span class="type-chip">{{ categoryLabel(row) }}</span>
            <span class="sev-pill" :class="severityClass(sev(row))">{{ prettyLabel(sev(row) || 'info') }}</span>
          </div>
        </template>
        <template v-slot:cell-part="{ row }">
          <a class="part-link" @click.stop="openPartDetailFromFinding(row)">{{ partLabel(row) }}</a>
        </template>
        <template v-slot:cell-field="{ row }">
          <code class="fieldpath">{{ fieldPath(row) || '—' }}</code>
          <span v-if="isAmazonPriceLookupFailure(row)" class="price-capture-chip">Amazon price unavailable</span>
        </template>
        <template v-slot:cell-current="{ row }">
          <span v-if="isEmptyVal(curVal(row))" class="val-empty">empty</span>
          <span v-else class="val cur">{{ fmtVal(curVal(row)) }}</span>
        </template>
        <template v-slot:cell-expected="{ row }">
          <span class="val exp">{{ fmtVal(expVal(row)) }}</span>
        </template>
        <template v-slot:cell-suggested="{ row }">
          <span class="val sug">{{ hasSuggestion(row) ? fmtVal(sugVal(row)) : '—' }}</span>
        </template>
        <template v-slot:cell-evidence="{ row }">
          <div class="evidence">
            <a
              v-if="evidenceSourceUrl(row)"
              class="evidence-summary"
              :href="evidenceSourceUrl(row)"
              target="_blank"
              rel="noopener"
              @click.stop
            >
              {{ evidenceSummary(row) }}
            </a>
            <span v-else class="evidence-summary" :title="evidenceFull(row)">{{ evidenceSummary(row) }}</span>
            <div v-if="findingActionState(row).label" class="action-state" :class="'state-' + findingActionState(row).tone">
              <strong>{{ findingActionState(row).label }}</strong>
              <span v-if="findingActionState(row).detail">{{ findingActionState(row).detail }}</span>
            </div>
          </div>
        </template>
        <template v-slot:cell-actions="{ row }">
          <AdminActionMenu v-if="isOpen(row) || canArchivePriceFinding(row)">
            <button
              class="row-btn review"
              :disabled="busy[fid(row)]"
              @click.stop="openFindingDetail(row)"
            >Review issue</button>
            <button
              v-if="hasSuggestion(row) && isOpen(row)"
              class="row-btn apply"
              :disabled="busy[fid(row)]"
              @click.stop="applySuggestion(row)"
            >Apply fix</button>
            <button
              v-if="isAmazonPriceLookupFailure(row)"
              class="row-btn price-retry"
              :disabled="!!runningJob"
              @click.stop="refreshMissingAmazonPrices"
            >Retry Amazon prices</button>
            <button
              v-if="canArchivePriceFinding(row)"
              class="row-btn archive"
              :disabled="busy[fid(row)]"
              @click.stop="archivePartForFinding(row)"
            >Archive part</button>
            <button
              v-if="isOpen(row)"
              class="row-btn"
              :disabled="busy[fid(row)]"
              @click.stop="researchFinding(row, 'rule')"
            >Rule research</button>
            <button
              v-if="isOpen(row)"
              class="row-btn"
              :disabled="busy[fid(row)]"
              @click.stop="researchFinding(row, 'ai')"
            >AI research</button>
            <button
              v-if="isOpen(row)"
              class="row-btn ignore danger"
              :disabled="busy[fid(row)]"
              @click.stop="ignoreFinding(row)"
            >Dismiss finding</button>
          </AdminActionMenu>
          <span v-else class="status-tag">{{ prettyLabel(statusOf(row)) }}</span>
        </template>
      </AdminTable>
      <table v-if="false" class="h-table">
        <thead>
          <tr>
            <th>Severity</th><th>Category</th><th>Part</th><th>Field</th><th>Current</th>
            <th>Expected</th><th>Suggested</th><th>Evidence</th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="f in visibleFindings" :key="fid(f)" class="finding-row" @click="openPartDetailFromFinding(f)">
            <td>
              <span class="sev-pill" :class="severityClass(sev(f))">{{ prettyLabel(sev(f) || 'info') }}</span>
            </td>
            <td>
              <span class="type-chip">{{ categoryLabel(f) }}</span>
            </td>
            <td>
              <a class="part-link" @click.stop="openPartDetailFromFinding(f)">{{ partLabel(f) }}</a>
              <span class="muted-sub">#{{ partId(f) }}</span>
            </td>
            <td><code class="fieldpath">{{ fieldPath(f) || '—' }}</code></td>
            <td class="val cur">
              <span v-if="isEmptyVal(curVal(f))" class="val-empty">empty</span>
              <span v-else>{{ fmtVal(curVal(f)) }}</span>
            </td>
            <td class="val exp">{{ fmtVal(expVal(f)) }}</td>
            <td class="val sug">{{ hasSuggestion(f) ? fmtVal(sugVal(f)) : '—' }}</td>
            <td class="evidence">
              <a
                v-if="evidenceSourceUrl(f)"
                class="evidence-summary"
                :href="evidenceSourceUrl(f)"
                target="_blank"
                rel="noopener"
                @click.stop
              >
                {{ evidenceSummary(f) }}
              </a>
              <span v-else class="evidence-summary" :title="evidenceFull(f)">{{ evidenceSummary(f) }}</span>
              <div v-if="findingActionState(f).label" class="action-state" :class="'state-' + findingActionState(f).tone">
                <strong>{{ findingActionState(f).label }}</strong>
                <span v-if="findingActionState(f).detail">{{ findingActionState(f).detail }}</span>
              </div>
            </td>
            <td class="f-actions">
              <button
                v-if="hasSuggestion(f) && isOpen(f)"
                class="row-btn apply"
                :disabled="busy[fid(f)]"
                @click.stop="applySuggestion(f)"
              >Apply fix</button>
              <button
                v-if="canArchivePriceFinding(f)"
                class="row-btn archive"
                :disabled="busy[fid(f)]"
                @click.stop="archivePartForFinding(f)"
              >Archive part</button>
              <button
                v-if="isOpen(f)"
                class="row-btn"
                :disabled="busy[fid(f)]"
                @click.stop="researchFinding(f, 'rule')"
              >Rule research</button>
              <button
                v-if="isOpen(f)"
                class="row-btn"
                :disabled="busy[fid(f)]"
                @click.stop="researchFinding(f, 'ai')"
              >AI research</button>
              <button
                v-if="isOpen(f)"
                class="row-btn compact"
                :disabled="busy[fid(f)]"
                @click.stop="researchFinding(f, 'auto')"
              >Auto</button>
              <button
                v-if="isOpen(f)"
                class="row-btn ignore"
                :disabled="busy[fid(f)]"
                @click.stop="ignoreFinding(f)"
              >Ignore</button>
              <span v-if="!isOpen(f)" class="status-tag">{{ prettyLabel(statusOf(f)) }}</span>
            </td>
          </tr>
          <tr v-if="!visibleFindings.length">
            <td colspan="9" class="empty">No findings for these filters.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Affected parts -->
    <div v-show="activeTab === 'parts'" class="catalog-table-section">
      <AdminTable
        class="health-admin-table"
        :columns="affectedPartColumns"
        :rows="parts"
        :row-key="partRowId"
        empty-text="No affected parts for these filters."
        clickable
        @row-click="openPartDetailFromRow"
      >
        <template v-slot:cell-part="{ row }">
          <a class="part-link" @click.stop="openPartDetailFromRow(row)">{{ partRowName(row) }}</a>
        </template>
        <template v-slot:cell-category="{ row }">
          <span class="type-chip">{{ prettyType(partRowType(row)) }}</span>
        </template>
        <template v-slot:cell-blockers="{ row }">
          <span class="count-pill tone-bad" :class="{ dim: !partBlockers(row) }">{{ partBlockers(row) }}</span>
        </template>
        <template v-slot:cell-warnings="{ row }">
          <span class="count-pill tone-warn" :class="{ dim: !partWarnings(row) }">{{ partWarnings(row) }}</span>
        </template>
        <template v-slot:cell-issues="{ row }">
          <div class="top-issues">
            <span v-for="(iss, idx) in topIssues(row)" :key="idx" class="issue-chip">{{ prettyLabel(iss) }}</span>
            <span v-if="!topIssues(row).length" class="muted-sub">—</span>
          </div>
        </template>
        <template v-slot:cell-actions="{ row }">
          <AdminActionMenu>
            <button class="row-btn" @click.stop="openPartDetailFromRow(row)">Details</button>
            <button class="row-btn" @click.stop="focusOnPartRow(row)">View findings</button>
          </AdminActionMenu>
        </template>
      </AdminTable>
      <table v-if="false" class="h-table">
        <thead>
          <tr>
            <th>Part</th><th>Category</th><th>Blockers</th><th>Warnings</th><th>Top issues</th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in parts" :key="partRowId(p)" class="finding-row" @click="openPartDetailFromRow(p)">
            <td><a class="part-link" @click.stop="openPartDetailFromRow(p)">{{ partRowName(p) }}</a></td>
            <td><span class="type-chip">{{ prettyType(partRowType(p)) }}</span></td>
            <td><span class="count-pill tone-bad" :class="{ dim: !partBlockers(p) }">{{ partBlockers(p) }}</span></td>
            <td><span class="count-pill tone-warn" :class="{ dim: !partWarnings(p) }">{{ partWarnings(p) }}</span></td>
            <td class="top-issues">
              <span v-for="(iss, idx) in topIssues(p)" :key="idx" class="issue-chip">{{ prettyLabel(iss) }}</span>
              <span v-if="!topIssues(p).length" class="muted-sub">—</span>
            </td>
            <td class="f-actions">
              <button class="row-btn" @click.stop="openPartDetailFromRow(p)">Details</button>
              <button class="row-btn" @click.stop="focusOnPartRow(p)">View findings</button>
            </td>
          </tr>
          <tr v-if="!parts.length">
            <td colspan="6" class="empty">No affected parts for these filters.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Audit history -->
    <div v-show="activeTab === 'audits'" class="catalog-table-section">
      <div class="audit-filters">
        <input class="hfilter grow" type="text" v-model="auditFilters.fieldPath"
               placeholder="Filter by field path (e.g. specs.socket)" @keyup.enter="fetchAudits" />
        <button class="btn-refresh" @click="fetchAudits">Apply</button>
        <a v-if="auditFilters.partId || auditFilters.fieldPath" class="focus-clear" @click="clearAuditFilters">clear</a>
      </div>
      <AdminTable
        class="health-admin-table"
        :columns="auditColumns"
        :rows="audits"
        :row-key="auditId"
        empty-text="No applied changes recorded yet."
      >
        <template v-slot:cell-when="{ row }">
          <span :title="fmtTime(auditWhen(row))">{{ fmtRelative(auditWhen(row)) }}</span>
        </template>
        <template v-slot:cell-part="{ row }">
          <span class="part-name">{{ auditPart(row) }}</span>
        </template>
        <template v-slot:cell-field="{ row }">
          <code class="fieldpath">{{ auditField(row) || '—' }}</code>
        </template>
        <template v-slot:cell-change="{ row }">
          <span class="change-cell">
            <span class="val old">{{ fmtVal(auditOld(row)) }}</span>
            <span class="arrow">→</span>
            <span class="val new">{{ fmtVal(auditNew(row)) }}</span>
          </span>
        </template>
        <template v-slot:cell-source="{ row }">
          <span class="src-tag">{{ prettyLabel(auditSource(row)) || '—' }}</span>
        </template>
        <template v-slot:cell-by="{ row }">
          <span class="muted-sub">{{ auditBy(row) || '—' }}</span>
        </template>
      </AdminTable>
      <table v-if="false" class="h-table">
        <thead>
          <tr>
            <th>When</th><th>Part</th><th>Field</th><th>Change</th><th>Source</th><th>By</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in audits" :key="auditId(a)">
            <td :title="fmtTime(auditWhen(a))">{{ fmtRelative(auditWhen(a)) }}</td>
            <td class="part-name">{{ auditPart(a) }}</td>
            <td><code class="fieldpath">{{ auditField(a) || '—' }}</code></td>
            <td class="change-cell">
              <span class="val old">{{ fmtVal(auditOld(a)) }}</span>
              <span class="arrow">→</span>
              <span class="val new">{{ fmtVal(auditNew(a)) }}</span>
            </td>
            <td><span class="src-tag">{{ prettyLabel(auditSource(a)) || '—' }}</span></td>
            <td class="muted-sub">{{ auditBy(a) || '—' }}</td>
          </tr>
          <tr v-if="!audits.length">
            <td colspan="6" class="empty">No applied changes recorded yet.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="selectedPart" class="part-drawer" @click.self="closePartDetail">
      <aside class="part-panel">
        <div class="part-panel-head">
          <div>
            <span class="type-chip">{{ prettyType(selectedPart.type) }}</span>
            <h5>{{ partDisplayName(selectedPart) }}</h5>
            <span class="muted-sub">
              Part #{{ selectedPart.id || '?' }}
              <span v-if="partAsin(selectedPart)"> - ASIN {{ partAsin(selectedPart) }}</span>
            </span>
          </div>
          <button class="panel-close" @click="closePartDetail">x</button>
        </div>

        <div class="part-panel-actions">
          <button class="gc-act primary" @click="viewSelectedPartFindings">View findings</button>
          <button class="gc-act" :disabled="editorBusy" @click="openPartEditor(selectedPart)">
            {{ editorBusy ? 'Opening...' : 'Open in editor' }}
          </button>
          <button class="gc-act" @click="openPartCategory(selectedPart)">Category list</button>
          <a v-if="partAmazonUrl(selectedPart)" class="gc-act drawer-link-action" :href="partAmazonUrl(selectedPart)" target="_blank" rel="noopener">
            Amazon
          </a>
        </div>

        <div v-if="selectedPartLoading" class="panel-loading">Loading catalog part details...</div>
        <div v-if="selectedPartError" class="panel-error">{{ selectedPartError }}</div>

        <div class="part-detail-overview" :class="{ 'no-thumb': !partImage(selectedPart) }">
          <div v-if="partImage(selectedPart)" class="part-detail-thumb" :style="{ backgroundImage: 'url(' + partImage(selectedPart) + ')' }"></div>
          <div class="part-detail-summary">
            <div v-for="row in partSummaryRows(selectedPart)" :key="row.label" class="part-detail-fact">
              <span>{{ row.label }}</span>
              <strong>{{ row.value }}</strong>
            </div>
          </div>
        </div>

        <div class="part-metrics">
          <div>
            <span class="metric-label">Open findings</span>
            <strong>{{ selectedPartFindings.length }}</strong>
          </div>
          <div>
            <span class="metric-label">Blockers</span>
            <strong class="metric-bad">{{ selectedPartSeverityCount('blocker') }}</strong>
          </div>
          <div>
            <span class="metric-label">Warnings</span>
            <strong class="metric-warn">{{ selectedPartSeverityCount('warning') }}</strong>
          </div>
        </div>

        <SpecEvaluationPanel
          :item="selectedPart"
          health-title="Part evaluation"
          health-description="Typed catalog evaluation using the same field model as Candidate Review."
          show-fields
          :show-research="false"
          :show-research-all="false"
          :show-verify-diff="false"
        />

        <div v-if="partReleaseRows(selectedPart).length" class="part-panel-section">
          <span class="section-kicker">Release date</span>
          <div class="part-spec-grid">
            <div v-for="row in partReleaseRows(selectedPart)" :key="row.key" class="part-spec-row">
              <span>{{ row.label }}</span>
              <a v-if="row.url" :href="row.url" target="_blank" rel="noopener">{{ row.value }}</a>
              <strong v-else>{{ row.value }}</strong>
            </div>
          </div>
        </div>

        <div
          v-for="section in partDetailSections(selectedPart)"
          :key="section.key"
          class="part-panel-section"
        >
          <span class="section-kicker">{{ section.label }}</span>
          <div class="part-spec-grid">
            <div v-for="row in section.rows" :key="row.key" class="part-spec-row">
              <span>{{ row.label }}</span>
              <strong>{{ row.value }}</strong>
            </div>
          </div>
        </div>

        <div class="part-panel-section">
          <span class="section-kicker">Top issues</span>
          <div class="top-issues">
            <span v-for="iss in selectedPartTopIssues" :key="iss" class="issue-chip">{{ iss }}</span>
            <span v-if="!selectedPartTopIssues.length" class="muted-sub">No open issues for current filters.</span>
          </div>
        </div>
        <div class="part-panel-section">
          <span class="section-kicker">Findings</span>
          <div class="drawer-findings">
            <div v-for="f in selectedPartFindings.slice(0, 8)" :key="fid(f)" class="drawer-finding drawer-finding-clickable" @click="openFindingDetail(f)">
              <span class="sev-pill" :class="severityClass(sev(f))">{{ prettyLabel(sev(f) || 'info') }}</span>
              <div>
                <code class="fieldpath">{{ fieldPath(f) || '-' }}</code>
                <p>{{ hasSuggestion(f) ? 'Suggested: ' + fmtVal(sugVal(f)) : evidenceSummary(f) }}</p>
              </div>
            </div>
            <div v-if="!selectedPartFindings.length" class="empty drawer-empty">No matching findings.</div>
          </div>
        </div>
      </aside>
    </div>

    <div v-if="selectedFinding" class="part-drawer review-modal-overlay" @click.self="closeFindingDetail">
      <aside class="part-panel finding-panel issue-review-panel">
        <div class="part-panel-head">
          <div>
            <span class="sev-pill" :class="severityClass(sev(selectedFinding))">{{ prettyLabel(sev(selectedFinding) || 'info') }}</span>
            <span class="type-chip drawer-type">{{ categoryLabel(selectedFinding) }}</span>
            <h5>Review catalog issue</h5>
            <span class="muted-sub">
              {{ partLabel(selectedFinding) }} - Finding #{{ fid(selectedFinding) }} - Part #{{ partId(selectedFinding) }}
            </span>
          </div>
          <button class="panel-close" @click="closeFindingDetail">x</button>
        </div>

        <div class="part-panel-actions">
          <button
            v-if="hasSuggestion(selectedFinding) && isOpen(selectedFinding)"
            class="gc-act primary"
            :disabled="busy[fid(selectedFinding)]"
            @click="applySuggestion(selectedFinding)"
          >Apply fix</button>
          <button
            v-if="isAmazonPriceLookupFailure(selectedFinding)"
            class="gc-act amazon-price-action"
            :disabled="!!runningJob"
            @click="refreshMissingAmazonPrices"
          >Retry Amazon prices</button>
          <button
            v-if="canArchivePriceFinding(selectedFinding)"
            class="gc-act archive"
            :disabled="busy[fid(selectedFinding)]"
            @click="archivePartForFinding(selectedFinding)"
          >Archive part</button>
          <button
            v-if="isOpen(selectedFinding)"
            class="gc-act"
            :disabled="busy[fid(selectedFinding)]"
            @click="researchFinding(selectedFinding, 'rule')"
          >Rule research</button>
          <button
            v-if="isOpen(selectedFinding)"
            class="gc-act"
            :disabled="busy[fid(selectedFinding)]"
            @click="researchFinding(selectedFinding, 'ai')"
          >AI research</button>
          <button
            v-if="isOpen(selectedFinding)"
            class="gc-act subtle"
            :disabled="busy[fid(selectedFinding)]"
            @click="researchFinding(selectedFinding, 'auto')"
          >Auto research</button>
          <button
            v-if="isOpen(selectedFinding)"
            class="gc-act danger-soft"
            :disabled="busy[fid(selectedFinding)]"
            @click="ignoreFinding(selectedFinding)"
          >Dismiss finding</button>
          <button class="gc-act" @click="openPartDetailFromFinding(selectedFinding)">Part details</button>
        </div>

        <div v-if="findingActionState(selectedFinding).label" class="drawer-state">
          <span class="action-state" :class="'state-' + findingActionState(selectedFinding).tone">
            <strong>{{ findingActionState(selectedFinding).label }}</strong>
            <span v-if="findingActionState(selectedFinding).detail">{{ findingActionState(selectedFinding).detail }}</span>
          </span>
        </div>

        <div class="issue-review-grid">
          <div class="issue-review-main">
            <div class="finding-summary-grid">
              <div>
                <span class="metric-label">Field</span>
                <code class="fieldpath">{{ fieldPath(selectedFinding) || '-' }}</code>
              </div>
              <div>
                <span class="metric-label">Issue</span>
                <strong>{{ findingIssueLabel(selectedFinding) }}</strong>
              </div>
              <div>
                <span class="metric-label">Status</span>
                <strong>{{ prettyLabel(statusOf(selectedFinding)) }}</strong>
              </div>
            </div>

            <div class="finding-values">
              <div>
                <span class="section-kicker">Current</span>
                <p>{{ fmtVal(curVal(selectedFinding)) }}</p>
              </div>
              <div>
                <span class="section-kicker">Expected</span>
                <p>{{ fmtVal(expVal(selectedFinding)) }}</p>
              </div>
              <div>
                <span class="section-kicker">Suggested</span>
                <p>{{ hasSuggestion(selectedFinding) ? fmtVal(sugVal(selectedFinding)) : 'No suggestion yet' }}</p>
              </div>
            </div>

            <div class="part-panel-section">
              <span class="section-kicker">Evidence</span>
              <div class="evidence-card">
                <a v-if="evidenceSourceUrl(selectedFinding)" :href="evidenceSourceUrl(selectedFinding)" target="_blank" rel="noopener">
                  Open source
                </a>
                <div v-for="row in findingEvidenceRows(selectedFinding)" :key="row.label" class="evidence-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.value }}</strong>
                </div>
                <div v-if="aiResponseRows(selectedFinding).length" class="ai-response-card">
                  <span class="section-kicker">AI response</span>
                  <div v-for="row in aiResponseRows(selectedFinding)" :key="row.key" class="ai-response-row">
                    <span>{{ row.label }}</span>
                    <pre>{{ row.value }}</pre>
                  </div>
                </div>
                <pre v-if="evidenceFull(selectedFinding)" class="evidence-raw">{{ evidenceFull(selectedFinding) }}</pre>
              </div>
            </div>
          </div>

          <div class="issue-review-side">
            <div class="part-review-card">
              <span class="section-kicker">Catalog part</span>
              <div v-if="selectedFindingPartLoading" class="panel-loading">Loading catalog part details...</div>
              <div v-if="selectedFindingPartError" class="panel-error">{{ selectedFindingPartError }}</div>
              <div v-if="selectedFindingPart" class="part-detail-overview compact" :class="{ 'no-thumb': !partImage(selectedFindingPart) }">
                <div v-if="partImage(selectedFindingPart)" class="part-detail-thumb" :style="{ backgroundImage: 'url(' + partImage(selectedFindingPart) + ')' }"></div>
                <div class="part-detail-summary">
                  <div v-for="row in partSummaryRows(selectedFindingPart)" :key="row.label" class="part-detail-fact">
                    <span>{{ row.label }}</span>
                    <strong>{{ row.value }}</strong>
                  </div>
                </div>
              </div>
              <div v-else-if="!selectedFindingPartLoading && !selectedFindingPartError" class="empty drawer-empty">Part detail unavailable.</div>
            </div>

            <SpecEvaluationPanel
              v-if="selectedFindingPart && !selectedFindingPartLoading"
              :item="selectedFindingPart"
              health-title="Part evaluation"
              health-description="Typed catalog evaluation for the affected live part."
              show-fields
              :show-research="false"
              :show-research-all="false"
              :show-verify-diff="false"
            />
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain, amazonProductUrl } from '../../config'
import realtime from '../../services/realtime'
import AdminActionMenu from './shared/AdminActionMenu.vue'
import AdminTable from './shared/AdminTable.vue'
import SpecEvaluationPanel from './shared/SpecEvaluationPanel.vue'
import { clearPageJobRun, loadPageJobRun, savePageJobRun } from '../../services/pageJobTracker'
import { describeCron } from '../../services/schedule'

const PAGE_JOB_KEY = 'catalog-health'

export default {
  name: 'CatalogHealth',
  components: {
    AdminActionMenu,
    AdminTable,
    SpecEvaluationPanel
  },
  data () {
    return {
      summary: null,
      findings: [],
      parts: [],
      audits: [],
      filters: { type: 'all', issue: 'all', severity: 'all', status: 'open', catalogScope: 'all' },
      // The scheduled "Catalog QA & Auto-Fix" pipeline (null until loaded).
      qaPipeline: null,
      auditFilters: { partId: '', fieldPath: '' },
      activeTab: 'findings',
      focusPart: null,
      focusPartName: '',
      error: null,
      notice: null,
      loading: false,
      runningJob: '',
      amazonPriceLimit: null,
      amazonPriceLimitTouched: false,
      amazonPriceEligibleCount: null,
      amazonPriceScopeLoading: false,
      amazonPriceScopeRequestId: 0,
      busy: {},
      actionResults: {},
      selectedPart: null,
      selectedPartLoading: false,
      selectedPartError: '',
      selectedPartRequestKey: '',
      selectedFinding: null,
      selectedFindingPart: null,
      selectedFindingPartLoading: false,
      selectedFindingPartError: '',
      selectedFindingPartRequestKey: '',
      editorBusy: false,
      trackedRun: null,
      trackedRunId: '',
      trackedJobKey: '',
      trackedJobLabel: '',
      unsubscribeTrackedRun: null,
      unsubscribeLiveState: null,
      liveFallbackTimer: null,
      liveConnected: false,
      lastAutoRefreshRunKey: '',
      partTypes: ['all', 'cpu', 'motherboard', 'ram', 'storage', 'gpu', 'psu', 'case'],
      catalogScopeOptions: [
        { value: 'active', label: 'Active online' },
        { value: 'unavailable', label: 'Unavailable only' },
        { value: 'archived', label: 'Archived only' },
        { value: 'all', label: 'All lifecycle' }
      ]
    }
  },
  computed: {
    /** Whether the catalog is looked after automatically, and how the last run went. */
    qaScheduleLine () {
      const pipeline = this.qaPipeline
      if (!pipeline) return ''
      if (!pipeline.enabled) return 'Automatic scan, research and fixes are off.'
      const when = describeCron(pipeline.cron) || 'on no schedule yet'
      const last = pipeline.lastRunAt
        ? ` · last run ${this.fmtRelative(pipeline.lastRunAt)}${pipeline.lastStatus ? ` (${this.prettyLabel(pipeline.lastStatus).toLowerCase()})` : ''}`
        : ' · not run yet'
      return `Scans, researches and applies safe fixes automatically, ${when}${last}.`
    },
    kpis () {
      const cats = this.categories
      const sum = (k) => cats.reduce((n, c) => n + (Number(c[k]) || 0), 0)
      // Prefer explicit numeric totals if the backend provides them; otherwise sum
      // across the per-type breakdown.
      const s = this.summary || {}
      const t = (s.totals && typeof s.totals === 'object') ? s.totals : s
      const parts = this.numOrNull(this.pick(t, ['totalParts', 'partCount', 'count'], null))
      const open = this.numOrNull(this.pick(t, ['openFindings', 'open', 'findingsOpen'], null))
      const blockers = this.numOrNull(this.pick(t, ['blockers', 'blocker', 'openBlockers'], null))
      const warnings = this.numOrNull(this.pick(t, ['warnings', 'warning', 'openWarnings'], null))
      return {
        parts: parts !== null ? parts : (cats.length ? sum('total') : '—'),
        open: open !== null ? open : (cats.length ? sum('open') : '—'),
        blockers: blockers !== null ? blockers : (cats.length ? sum('blockers') : '—'),
        warnings: warnings !== null ? warnings : (cats.length ? sum('warnings') : '—')
      }
    },
    categories () {
      const s = this.summary || {}
      // Array form (byType/categories returned as a list).
      const arr = s.byType || s.categories || s.drilldown
      if (Array.isArray(arr)) {
        return arr.map((r) => this.normCat(r, this.pick(r, ['type', 'partType', 'category', 'key'], '—')))
      }
      // Object form keyed by part type (the shape this backend returns).
      const map = this.typeStatsMap()
      return Object.keys(map).map((k) => this.normCat(map[k], k))
    },
    issueOptions () {
      const set = {}
      this.findings.forEach((f) => {
        const i = this.issueOf(f)
        if (i) set[i] = true
      })
      return Object.keys(set).sort()
    },
    visibleFindings () {
      if (!this.focusPart) return this.findings
      return this.findings.filter((f) => String(this.partId(f)) === String(this.focusPart))
    },
    selectedPartFindings () {
      if (!this.selectedPart) return []
      return this.findings.filter((f) => String(this.partId(f)) === String(this.selectedPart.id))
    },
    selectedPartTopIssues () {
      const seen = {}
      this.selectedPartFindings.forEach((f) => {
        const issue = this.findingIssueLabel(f)
        if (issue) seen[issue] = true
      })
      return Object.keys(seen).slice(0, 6)
    },
    amazonPriceFindingCount () {
      const parts = {}
      this.findings.forEach((finding) => {
        if (!this.isAmazonPriceFinding(finding)) return
        const partId = this.partId(finding)
        const key = partId
          ? `${this.catalogPartType(this.typeOf(finding))}:${partId}`
          : `finding:${this.fid(finding)}`
        parts[key] = true
      })
      return Object.keys(parts).length
    },
    validAmazonPriceLimit () {
      const limit = Number(this.amazonPriceLimit)
      return Number.isInteger(limit) && limit > 0
    },
    findingColumns () {
      return [
        { key: 'category', label: 'Category', width: '150px', breakable: true, sortable: true, sortAccessor: (f) => this.categoryLabel(f) },
        { key: 'part', label: 'Part', width: '190px', breakable: true, sortable: true, sortAccessor: (f) => this.partLabel(f) },
        { key: 'field', label: 'Field', width: '166px', nowrap: true, sortable: true, sortAccessor: (f) => this.fieldPath(f) },
        { key: 'current', label: 'Current', width: '120px', breakable: true },
        { key: 'expected', label: 'Expected', width: '130px', breakable: true },
        { key: 'suggested', label: 'Suggested', width: '96px', breakable: true },
        { key: 'evidence', label: 'Evidence', width: '218px', breakable: true },
        { key: 'actions', label: '', width: '116px', align: 'right', nowrap: true }
      ]
    },
    affectedPartColumns () {
      return [
        { key: 'part', label: 'Part', width: '30%', breakable: true, sortable: true, defaultDir: 'asc', sortAccessor: (p) => this.partRowName(p) },
        { key: 'category', label: 'Category', width: '112px', nowrap: true, sortable: true, sortAccessor: (p) => this.prettyType(this.partRowType(p)) },
        { key: 'blockers', label: 'Blockers', width: '100px', nowrap: true, sortable: true, sortAccessor: (p) => Number(this.partBlockers(p)) || 0 },
        { key: 'warnings', label: 'Warnings', width: '104px', nowrap: true, sortable: true, sortAccessor: (p) => Number(this.partWarnings(p)) || 0 },
        { key: 'issues', label: 'Top issues', width: '34%', breakable: true },
        { key: 'actions', label: '', width: '190px', align: 'right', nowrap: true }
      ]
    },
    auditColumns () {
      return [
        { key: 'when', label: 'When', width: '120px', nowrap: true, sortable: true, sortAccessor: (a) => new Date(this.auditWhen(a)).getTime() || 0 },
        { key: 'part', label: 'Part', width: '180px', breakable: true, sortable: true, sortAccessor: (a) => this.auditPart(a) },
        { key: 'field', label: 'Field', width: '150px', breakable: true, sortable: true, sortAccessor: (a) => this.auditField(a) },
        { key: 'change', label: 'Change', width: '34%', breakable: true },
        { key: 'source', label: 'Source', width: '120px', nowrap: true, sortable: true, sortAccessor: (a) => this.auditSource(a) },
        { key: 'by', label: 'By', width: '120px', breakable: true, sortable: true, sortAccessor: (a) => this.auditBy(a) }
      ]
    }
  },
  mounted () {
    this.fetchAll()
    this.restoreTrackedRun()
  },
  beforeDestroy () {
    this.stopJobPolling()
  },
  methods: {
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    queryString () {
      const f = this.filters
      const p = new URLSearchParams()
      p.set('type', f.type || 'all')
      p.set('issue', f.issue || 'all')
      p.set('severity', f.severity || 'all')
      p.set('status', f.status || 'open')
      p.set('catalogScope', f.catalogScope || 'all')
      p.set('limit', '100')
      return p.toString()
    },
    summaryQueryString () {
      const f = this.filters
      const p = new URLSearchParams()
      p.set('type', f.type || 'all')
      p.set('issue', f.issue || 'all')
      p.set('severity', f.severity || 'all')
      p.set('status', f.status || 'open')
      p.set('catalogScope', f.catalogScope || 'all')
      return p.toString()
    },
    async fetchAll () {
      this.loading = true
      try {
        const [summaryRes] = await Promise.all([
          axios.get(`${apiDomain}catalog-health/summary?${this.summaryQueryString()}`, { headers: this.authHeaders() }),
          this.fetchFindings(),
          this.fetchParts(),
          this.fetchAudits()
        ])
        this.summary = this.unwrap(summaryRes.data, ['summary', 'data']) || summaryRes.data
        this.error = null
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.loading = false
      }
      this.fetchAmazonPriceScope()
      this.fetchQaPipeline()
    },
    // The scheduled Catalog QA pipeline, for the "runs automatically" line.
    async fetchQaPipeline () {
      try {
        const { data } = await axios.get(`${apiDomain}pipelines/catalog-qa`, { headers: this.authHeaders() })
        this.qaPipeline = (data && data.pipeline) || null
      } catch (e) {
        this.qaPipeline = null
      }
    },
    async fetchFindings () {
      try {
        const { data } = await axios.get(
          `${apiDomain}catalog-health/findings?${this.queryString()}`,
          { headers: this.authHeaders() })
        this.findings = this.asList(data, ['findings', 'items', 'data', 'results'])
      } catch (e) { this.error = this.errMsg(e) }
    },
    async fetchParts () {
      try {
        const { data } = await axios.get(
          `${apiDomain}catalog-health/parts?${this.queryString()}`,
          { headers: this.authHeaders() })
        this.parts = this.asList(data, ['parts', 'items', 'data', 'results'])
      } catch (e) { this.error = this.errMsg(e) }
    },
    async fetchAudits () {
      try {
        const p = new URLSearchParams()
        p.set('type', this.filters.type || 'all')
        if (this.auditFilters.partId) p.set('partId', this.auditFilters.partId)
        if (this.auditFilters.fieldPath) p.set('fieldPath', this.auditFilters.fieldPath)
        p.set('limit', '100')
        const { data } = await axios.get(
          `${apiDomain}catalog-health/audits?${p.toString()}`,
          { headers: this.authHeaders() })
        this.audits = this.asList(data, ['audits', 'items', 'data', 'results', 'history'])
      } catch (e) { this.error = this.errMsg(e) }
    },
    onFilterChange () {
      this.refreshSummary()
      this.fetchFindings()
      this.fetchParts()
      this.fetchAmazonPriceScope()
    },
    async runJob (key, options, label, runKey) {
      this.runningJob = runKey || key
      this.trackedJobKey = key
      this.trackedJobLabel = label
      this.trackedRun = {
        id: '',
        jobKey: key,
        status: 'queued',
        startedAt: new Date().toISOString(),
        stats: {},
        label,
        runKey: runKey || key
      }
      this.persistTrackedRun()
      this.notice = null
      try {
        const { data } = await axios.post(`${apiDomain}jobs/${key}/run`,
          { options: options || {} }, { headers: this.authHeaders() })
        const run = this.extractRun(data)
        if (run) {
          this.trackedRun = Object.assign({}, this.trackedRun, run, { label, runKey: runKey || key })
          this.trackedRunId = this.runId(run)
          this.persistTrackedRun()
        }
        this.notice = `${label} started. Tracking progress on this page.`
        this.error = null
        this.ensureJobPolling()
      } catch (e) {
        this.error = this.errMsg(e)
        if (e && e.response && e.response.status >= 400 && e.response.status < 500) {
          this.runningJob = ''
          this.trackedRun = null
          this.trackedRunId = ''
          clearPageJobRun(PAGE_JOB_KEY, 'primary')
        } else {
          this.ensureJobPolling()
        }
      }
    },
    lifecycleJobOptions (options) {
      return Object.assign({ catalogScope: this.filters.catalogScope || 'all' }, options || {})
    },
    runScan () {
      this.runJob('catalog-health-scan', this.lifecycleJobOptions(), 'Catalog scan')
    },
    // Fixes act on what the table shows: the selected part type, and every
    // suggestion in it — AI-researched ones included, since applying them from
    // here is the review. (The scheduled job stays deterministic-only.)
    fixJobOptions (dryRun) {
      const type = this.catalogPartType(this.filters.type)
      return this.lifecycleJobOptions(Object.assign({ dryRun, includeAiSuggestions: true }, type !== 'all' ? { type } : {}))
    },
    dryRunFixes () {
      this.runJob('auto-fix-part-standards', this.fixJobOptions(true), 'Dry-run', 'auto-fix-part-standards-dry')
    },
    applyFixes () {
      const type = this.catalogPartType(this.filters.type)
      const scope = type !== 'all' ? `${this.prettyLabel(this.filters.type)} parts` : 'all parts'
      if (!window.confirm(`Apply every suggested fix for ${scope}?\n\nThis includes AI-researched suggestions, so check them in the table first.`)) return
      this.runJob('auto-fix-part-standards', this.fixJobOptions(false), 'Apply fixes')
    },
    aiResearch () {
      this.runJob('ai-research-part-health-fixes', this.lifecycleJobOptions(), 'AI research')
    },
    repairImages () {
      this.runJob('repair-missing-images', this.lifecycleJobOptions(), 'Image repair')
    },
    amazonPriceJobOptions (limit) {
      const selectedType = this.filters.type && this.filters.type !== 'all'
        ? this.catalogPartType(this.filters.type)
        : 'all'
      const options = {
        type: selectedType,
        status: 'all',
        statuses: ['discovered', 'needs_review', 'validated', 'scrape_failed', 'rejected', 'imported'],
        missingOnly: true,
        reopen: false,
        delayMs: 4000
      }
      if (limit !== undefined && limit !== null) options.limit = limit
      return options
    },
    async fetchAmazonPriceScope () {
      const requestId = ++this.amazonPriceScopeRequestId
      this.amazonPriceScopeLoading = true
      try {
        const params = new URLSearchParams()
        const options = this.amazonPriceJobOptions()
        Object.keys(options).forEach((key) => {
          const value = options[key]
          if (value === undefined || value === null || value === '') return
          params.set(key, Array.isArray(value) ? value.join(',') : String(value))
        })
        params.set('previewLimit', '1')
        const { data } = await axios.get(
          `${apiDomain}jobs/refresh-candidate-prices/scope?${params.toString()}`,
          { headers: this.authHeaders() })
        if (requestId !== this.amazonPriceScopeRequestId) return

        const scope = data && (data.scope || data)
        const eligible = this.amazonPriceScopeCount(scope)
        this.amazonPriceEligibleCount = eligible !== null ? eligible : this.amazonPriceFindingCount
        if (!this.amazonPriceLimitTouched) this.amazonPriceLimit = this.amazonPriceEligibleCount
      } catch (e) {
        if (requestId !== this.amazonPriceScopeRequestId) return
        this.amazonPriceEligibleCount = this.amazonPriceFindingCount
        if (!this.amazonPriceLimitTouched) this.amazonPriceLimit = this.amazonPriceFindingCount
      } finally {
        if (requestId === this.amazonPriceScopeRequestId) this.amazonPriceScopeLoading = false
      }
    },
    amazonPriceScopeCount (scope) {
      if (!scope || typeof scope !== 'object') return null
      const containers = [
        scope,
        scope.counts,
        scope.summary,
        scope.descriptor
      ].filter((value) => value && typeof value === 'object' && !Array.isArray(value))
      const keys = [
        'eligible',
        'totalEligible',
        'total_eligible',
        'missingPrices',
        'missing_prices',
        'missingPrice',
        'missing_price',
        'missing',
        'total'
      ]
      for (const container of containers) {
        const count = this.numOrNull(this.pick(container, keys, null))
        if (count !== null && count >= 0) return Math.floor(count)
      }
      return null
    },
    normalizeAmazonPriceLimit () {
      const limit = Number(this.amazonPriceLimit)
      if (Number.isInteger(limit) && limit > 0) return
      const fallback = this.amazonPriceEligibleCount !== null
        ? this.amazonPriceEligibleCount
        : this.amazonPriceFindingCount
      this.amazonPriceLimit = fallback > 0 ? fallback : null
    },
    refreshMissingAmazonPrices () {
      this.normalizeAmazonPriceLimit()
      if (!this.validAmazonPriceLimit) return
      this.runJob(
        'refresh-candidate-prices',
        this.amazonPriceJobOptions(Number(this.amazonPriceLimit)),
        'Amazon price refresh')
    },
    async ignoreFinding (f) {
      const id = this.fid(f)
      const ok = window.confirm(`Dismiss this finding for ${this.partLabel(f)}?\n\nIt will move out of the open findings list.`)
      if (!ok) return
      this.$set(this.busy, id, true)
      try {
        await axios.post(`${apiDomain}catalog-health/findings/${id}/ignore`, {},
          { headers: this.authHeaders() })
        this.$set(this.actionResults, id, { label: 'Finding ignored', tone: 'info', detail: '' })
        await this.refreshFindingCollections(id)
      } catch (e) { this.error = this.errMsg(e) } finally { this.$set(this.busy, id, false) }
    },
    async researchFinding (f, mode) {
      const id = this.fid(f)
      this.$set(this.busy, id, mode)
      try {
        const { data } = await axios.post(`${apiDomain}catalog-health/findings/${id}/research`,
          { mode }, { headers: this.authHeaders() })
        await this.handleFindingActionResponse(id, data, 'research')
      } catch (e) {
        this.error = this.errMsg(e)
        this.$set(this.actionResults, id, {
          label: 'Research failed',
          tone: 'bad',
          detail: this.errMsg(e)
        })
      } finally {
        this.$set(this.busy, id, false)
      }
    },
    async applySuggestion (f) {
      const id = this.fid(f)
      if (!window.confirm(`Apply suggested value to ${this.fieldPath(f)} on ${this.partLabel(f)}?`)) return
      this.$set(this.busy, id, true)
      try {
        const { data } = await axios.post(`${apiDomain}catalog-health/findings/${id}/apply-suggestion`, {},
          { headers: this.authHeaders() })
        await this.handleFindingActionResponse(id, data, 'apply')
        await this.fetchAudits()
      } catch (e) { this.error = this.errMsg(e) } finally { this.$set(this.busy, id, false) }
    },
    async archivePartForFinding (f) {
      const id = this.fid(f)
      const type = this.catalogPartType(this.typeOf(f) || (this.filters.type !== 'all' ? this.filters.type : ''))
      const partId = this.partId(f)

      if (!id || !type || !partId) {
        this.error = 'This part cannot be archived because the finding is missing a part id or category.'
        return
      }

      const partName = this.partLabel(f)
      const ok = window.confirm(
        `Archive ${partName}?\n\nUse this when Amazon cannot match the listing price and the part should be removed from active catalog/builder results.`
      )
      if (!ok) return

      this.$set(this.busy, id, 'archive')
      try {
        const payload = {
          findingId: id,
          partId,
          type,
          field: this.fieldPath(f),
          reason: 'amzn_price could not match'
        }
        const data = await this.archiveCatalogPartForFinding(id, type, partId, payload)
        await this.handleFindingActionResponse(id, data, 'archive')
        await Promise.all([this.fetchParts(), this.fetchAudits()])
      } catch (e) {
        this.error = this.errMsg(e)
        this.$set(this.actionResults, id, {
          label: 'Archive failed',
          tone: 'bad',
          detail: this.errMsg(e)
        })
      } finally {
        this.$set(this.busy, id, false)
      }
    },
    async archiveCatalogPartForFinding (findingId, type, partId, payload) {
      try {
        const { data } = await axios.post(
          `${apiDomain}catalog-health/findings/${encodeURIComponent(findingId)}/archive-part`,
          payload,
          { headers: this.authHeaders() }
        )
        return data
      } catch (e) {
        const status = e && e.response ? e.response.status : 0
        if (status !== 404 && status !== 405) throw e
      }

      const { data } = await axios.post(
        `${apiDomain}catalog-parts/${encodeURIComponent(type)}/${encodeURIComponent(partId)}/archive`,
        payload,
        { headers: this.authHeaders() }
      )
      return data
    },
    async handleFindingActionResponse (id, data, action) {
      const result = this.actionResultFromResponse(data, action)
      this.$set(this.actionResults, id, result)
      if (result.label) {
        this.notice = result.label
      }

      if (data && data.resolved) {
        this.findings = this.findings.filter((f) => String(this.fid(f)) !== String(id))
        await this.refreshFindingCollections(id)
        return
      }

      if (data && data.finding) {
        this.replaceFindingRow(id, data.finding)
      }

      await this.refreshFindingCollections(id)
    },
    async refreshFindingCollections (actionFindingId) {
      const selectedId = this.selectedFinding ? this.fid(this.selectedFinding) : ''
      await Promise.all([this.fetchFindings(), this.refreshSummary(), this.fetchParts()])

      if (!selectedId) return

      const refreshed = this.findings.find((f) => String(this.fid(f)) === String(selectedId))
      if (refreshed) {
        this.selectedFinding = refreshed
        if (this.selectedFindingPart) {
          await this.fetchSelectedFindingPart(refreshed)
        }
        return
      }

      if (!actionFindingId || String(actionFindingId) === String(selectedId)) {
        this.closeFindingDetail()
      }
    },
    replaceFindingRow (id, finding) {
      const next = this.findings.slice()
      const index = next.findIndex((f) => String(this.fid(f)) === String(id))
      if (index >= 0) {
        this.$set(next, index, finding)
        this.findings = next
        if (this.selectedFinding && String(this.fid(this.selectedFinding)) === String(id)) {
          this.selectedFinding = finding
        }
        return
      }
      this.findings = [finding].concat(next)
      if (this.selectedFinding && String(this.fid(this.selectedFinding)) === String(id)) {
        this.selectedFinding = finding
      }
    },
    async refreshSummary () {
      try {
        const { data } = await axios.get(`${apiDomain}catalog-health/summary?${this.summaryQueryString()}`,
          { headers: this.authHeaders() })
        this.summary = this.unwrap(data, ['summary', 'data']) || data
      } catch (e) { /* keep last snapshot */ }
    },
    selectType (type) {
      this.filters.type = type
      this.clearFocus()
      this.activeTab = 'findings'
      this.onFilterChange()
    },
    focusOnPart (f) {
      this.focusPart = this.partId(f)
      this.focusPartName = this.partLabel(f)
      this.activeTab = 'findings'
    },
    focusOnPartRow (p) {
      this.focusPart = this.partRowId(p)
      this.focusPartName = this.partRowName(p)
      this.activeTab = 'findings'
    },
    openPartDetailFromFinding (f) {
      this.selectedFinding = null
      this.clearSelectedFindingPart()
      this.openPartDetail(this.partModelFromFinding(f))
    },
    openFindingDetail (f) {
      this.selectedFinding = f
      this.selectedPart = null
      this.selectedPartError = ''
      this.fetchSelectedFindingPart(f)
    },
    closeFindingDetail () {
      this.selectedFinding = null
      this.clearSelectedFindingPart()
    },
    clearSelectedFindingPart () {
      this.selectedFindingPart = null
      this.selectedFindingPartError = ''
      this.selectedFindingPartRequestKey = ''
      this.selectedFindingPartLoading = false
    },
    async fetchSelectedFindingPart (finding) {
      const part = this.partModelFromFinding(finding)
      const id = part && part.id
      const type = this.catalogPartType(part && part.type)
      if (!id || !type) {
        this.selectedFindingPart = part
        this.selectedFindingPartError = 'Part details cannot be loaded because the finding is missing a part id or category.'
        return
      }

      const requestKey = `${type}:${id}:${Date.now()}`
      this.selectedFindingPart = part
      this.selectedFindingPartRequestKey = requestKey
      this.selectedFindingPartLoading = true
      this.selectedFindingPartError = ''

      try {
        const { data } = await axios.get(
          `${apiDomain}catalog-parts/${encodeURIComponent(type)}/${encodeURIComponent(id)}`,
          { headers: this.authHeaders() }
        )
        if (this.selectedFindingPartRequestKey !== requestKey) return
        const detail = this.unwrap(data, ['part', 'data']) || data
        this.selectedFindingPart = this.mergePartDetail(part, detail)
      } catch (e) {
        if (this.selectedFindingPartRequestKey === requestKey) {
          this.selectedFindingPartError = this.errMsg(e)
        }
      } finally {
        if (this.selectedFindingPartRequestKey === requestKey) {
          this.selectedFindingPartLoading = false
        }
      }
    },
    openPartDetailFromRow (p) {
      this.openPartDetail(this.partModelFromRow(p))
    },
    openPartDetail (part) {
      if (!part) return
      this.selectedPart = part
      this.selectedPartError = ''
      this.fetchCatalogPartDetail(part)
    },
    closePartDetail () {
      this.selectedPart = null
      this.selectedPartError = ''
      this.selectedPartRequestKey = ''
    },
    viewSelectedPartFindings () {
      if (!this.selectedPart) return
      this.focusPart = this.selectedPart.id
      this.focusPartName = this.selectedPart.name
      this.activeTab = 'findings'
      this.closePartDetail()
    },
    async openPartEditor (part) {
      if (!part) return
      const type = this.adminListType(part.type)
      const search = this.partAsin(part) || this.partDisplayName(part)
      this.editorBusy = true
      try {
        this.$router.push({
          path: '/users/items-controller/items',
          query: search ? { type, search } : { type }
        }).catch(() => {})
      } finally {
        this.editorBusy = false
      }
    },
    openPartCategory (part) {
      if (!part) return
      this.$router.push({
        path: '/users/items-controller/items',
        query: { type: this.adminListType(part.type) }
      }).catch(() => {})
    },
    async fetchCatalogPartDetail (part) {
      const id = part && part.id
      const type = this.catalogPartType(part && part.type)
      if (!id || !type) {
        this.selectedPartError = 'Part details cannot be loaded because the part id or category is missing.'
        return
      }

      const requestKey = `${type}:${id}:${Date.now()}`
      this.selectedPartRequestKey = requestKey
      this.selectedPartLoading = true
      this.selectedPartError = ''

      try {
        const { data } = await axios.get(
          `${apiDomain}catalog-parts/${encodeURIComponent(type)}/${encodeURIComponent(id)}`,
          { headers: this.authHeaders() }
        )
        if (this.selectedPartRequestKey !== requestKey) return
        const detail = this.unwrap(data, ['part', 'data']) || data
        this.selectedPart = this.mergePartDetail(part, detail)
      } catch (e) {
        if (this.selectedPartRequestKey === requestKey) {
          this.selectedPartError = this.errMsg(e)
        }
      } finally {
        if (this.selectedPartRequestKey === requestKey) {
          this.selectedPartLoading = false
        }
      }
    },
    clearFocus () {
      this.focusPart = null
      this.focusPartName = ''
    },
    clearAuditFilters () {
      this.auditFilters.partId = ''
      this.auditFilters.fieldPath = ''
      this.fetchAudits()
    },
    ensureJobPolling () {
      this.stopJobPolling()
      this.liveConnected = realtime.isConnected()
      this.unsubscribeLiveState = realtime.onStateChange((state, connected, detail) => {
        this.liveConnected = !!connected
        if (connected && state === 'open') {
          if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
          this.liveFallbackTimer = null
          this.error = null
          if (this.trackedRunId) realtime.refresh(`jobs:${this.trackedRunId}`)
        } else {
          if (state === 'error') this.error = realtime.errorMessage(detail)
          this.scheduleFallbackPoll(500)
        }
      })
      if (this.trackedRunId) {
        const topic = `jobs:${this.trackedRunId}`
        this.unsubscribeTrackedRun = realtime.subscribe(topic, {
          'job:snapshot': this.handleTrackedRunEvent,
          'job:progress': this.handleTrackedRunProgress,
          'job:finished': this.handleTrackedRunEvent,
          'job:error': this.handleTrackedRunError
        })
        if (realtime.isConnected()) realtime.refresh(topic)
      }
      this.scheduleFallbackPoll(5000)
    },
    stopJobPolling () {
      if (this.unsubscribeTrackedRun) this.unsubscribeTrackedRun()
      if (this.unsubscribeLiveState) this.unsubscribeLiveState()
      if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
      this.unsubscribeTrackedRun = null
      this.unsubscribeLiveState = null
      this.liveFallbackTimer = null
    },
    scheduleFallbackPoll (delay) {
      if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
      this.liveFallbackTimer = null
      if (realtime.isConnected() || !this.trackedRun || !this.isRunActive(this.trackedRun)) return
      this.liveFallbackTimer = setTimeout(async () => {
        this.liveFallbackTimer = null
        if (realtime.isConnected()) return
        await this.refreshTrackedRun()
        if (!realtime.isConnected() && this.trackedRun && this.isRunActive(this.trackedRun)) {
          this.scheduleFallbackPoll(2500)
        }
      }, delay || 2500)
    },
    handleTrackedRunEvent (payload) {
      const run = this.extractRun(payload)
      if (!run) return
      this.applyTrackedRun(run)
    },
    handleTrackedRunError (payload) {
      const data = payload || {}
      this.error = data.message || data.reason || realtime.errorMessage(data)
      const run = this.extractRun(data)
      if (run) this.applyTrackedRun(Object.assign({ status: 'failed' }, run))
    },
    handleTrackedRunProgress (payload) {
      const data = payload || {}
      if (this.trackedRun && !this.isRunActive(this.trackedRun)) return
      const run = this.extractRun(data) || {
        id: data.runId || data.run_id || data.id || this.trackedRunId,
        jobKey: this.trackedJobKey,
        status: data.status || (this.trackedRun && this.trackedRun.status) || 'running',
        stats: data.stats || data.progressStats,
        progress: data.progress,
        percent: data.percent || data.progressPercent
      }
      this.applyTrackedRun(run)
    },
    async applyTrackedRun (incoming) {
      const run = Object.assign({}, this.trackedRun || {}, incoming || {})
      if (!run) return
      this.trackedRun = run
      this.trackedRunId = this.runId(run)
      this.persistTrackedRun()
      if (!this.isRunActive(run)) {
        this.runningJob = ''
        this.stopJobPolling()
        await this.refreshAfterTrackedRun(run)
      }
    },
    async refreshAfterTrackedRun (run) {
      const runKey = this.runId(run)
        ? `run:${this.runId(run)}`
        : `${this.runJobKey(run)}:${this.runStartedAt(run)}`
      if (!runKey || this.lastAutoRefreshRunKey === runKey) return

      this.lastAutoRefreshRunKey = runKey
      await this.fetchAll()

      const status = String(this.runStatus(run) || '').toLowerCase()
      const succeeded = ['success', 'succeeded', 'completed', 'complete'].indexOf(status) >= 0
      const label = this.trackedJobLabel || this.prettyLabel(this.runJobKey(run)) || 'Job'
      this.notice = succeeded
        ? (this.fixRunSummary(run) || `${label} completed. Catalog health updated automatically.`)
        : `${label} finished with ${this.prettyLabel(status)}. Catalog health updated automatically.`
    },
    // What a fix run did, so "Apply fixes" never ends on a bare "completed".
    fixRunSummary (run) {
      if (this.runJobKey(run) !== 'auto-fix-part-standards') return ''
      let stats = run && run.stats
      if (typeof stats === 'string') {
        try { stats = JSON.parse(stats) } catch (e) { stats = null }
      }
      if (!stats || stats.checked === undefined) return ''
      const checked = Number(stats.checked) || 0
      const plural = (n) => `${n} fix${n === 1 ? '' : 'es'}`
      if (!checked) return 'No suggested fixes to apply for this selection.'
      if (stats.dryRun === true || stats.dryRun === 'true') return `Dry-run: ${plural(checked)} would be applied.`
      const applied = Number(stats.applied) || 0
      const skipped = Number(stats.skipped) || 0
      return `Applied ${applied} of ${plural(checked)}${skipped ? `; ${skipped} skipped (see the job log)` : ''}. Catalog health updated.`
    },
    persistTrackedRun () {
      if (!this.trackedRun) return
      savePageJobRun(PAGE_JOB_KEY, 'primary', Object.assign({}, this.trackedRun, {
        jobKey: this.trackedJobKey || this.runJobKey(this.trackedRun),
        label: this.trackedJobLabel,
        runKey: this.runningJob || this.trackedRun.runKey || this.trackedJobKey
      }))
    },
    async restoreTrackedRun () {
      const saved = loadPageJobRun(PAGE_JOB_KEY, 'primary')
      if (!saved) return
      this.trackedRunId = saved.runId || ''
      this.trackedJobKey = saved.jobKey || ''
      this.trackedJobLabel = saved.label || ''
      this.trackedRun = Object.assign({ stats: {} }, saved, { id: saved.runId || '' })
      this.runningJob = this.isRunActive(this.trackedRun) ? (saved.runKey || saved.jobKey || '') : ''

      if (!saved.runId) {
        this.ensureJobPolling()
        await this.refreshTrackedRun()
        return
      }
      try {
        const { data } = await axios.get(`${apiDomain}jobs/runs/${saved.runId}`, { headers: this.authHeaders() })
        const run = this.extractRun(data)
        if (run) {
          this.trackedRun = Object.assign({}, saved, run)
          this.trackedRunId = this.runId(run)
          this.runningJob = this.isRunActive(run) ? (saved.runKey || saved.jobKey || '') : ''
          this.persistTrackedRun()
          if (this.isRunActive(run)) this.ensureJobPolling()
        }
      } catch (e) {
        if (e && e.response && e.response.status === 404) {
          clearPageJobRun(PAGE_JOB_KEY, 'primary')
          this.trackedRun = null
          this.trackedRunId = ''
          this.trackedJobKey = ''
          this.runningJob = ''
        } else {
          this.ensureJobPolling()
        }
      }
    },
    async refreshTrackedRun () {
      if (!this.trackedJobKey && !this.trackedRunId) return
      try {
        let match = null
        if (this.trackedRunId) {
          const { data } = await axios.get(`${apiDomain}jobs/runs/${this.trackedRunId}`, { headers: this.authHeaders() })
          match = this.extractRun(data)
        } else {
          const { data } = await axios.get(`${apiDomain}jobs/runs?limit=25`, { headers: this.authHeaders() })
          const runs = this.asList(data, ['runs', 'items', 'data', 'results'])
          match = this.findTrackedRun(runs)
        }
        if (match) {
          await this.applyTrackedRun(match)
        }
      } catch (e) {
        if (e && e.response && e.response.status === 404) {
          clearPageJobRun(PAGE_JOB_KEY, 'primary')
          this.trackedRun = null
          this.trackedRunId = ''
          this.trackedJobKey = ''
          this.runningJob = ''
          this.stopJobPolling()
        }
        // Keep the visible run on transient polling failures.
      }
    },
    findTrackedRun (runs) {
      if (!Array.isArray(runs) || !runs.length) return null
      if (this.trackedRunId) {
        const exact = runs.find((run) => String(this.runId(run)) === String(this.trackedRunId))
        if (exact) return exact
      }
      const sameJob = runs.filter((run) => this.runJobKey(run) === this.trackedJobKey)
      const active = sameJob.find((run) => this.isRunActive(run))
      return active || sameJob[0] || null
    },
    extractRun (data) {
      if (!data) return null
      if (data.run) return data.run
      if (data.jobRun) return data.jobRun
      if (data.data && (data.data.id || data.data.runId)) return data.data
      if (data.id || data.runId) return data
      return null
    },

    // ---- tolerant accessors ----
    pick (obj, keys, fallback) {
      if (!obj) return fallback
      for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null) return obj[k]
      }
      return fallback
    },
    unwrap (data, keys) {
      for (const k of keys) {
        if (data && data[k] !== undefined && data[k] !== null) return data[k]
      }
      return null
    },
    pickDeep (obj, keys) {
      const direct = this.pick(obj, keys, undefined)
      if (direct !== undefined && direct !== null) return direct
      const nests = ['detail', 'details', 'data', 'meta', 'context', 'finding']
      for (const n of nests) {
        if (obj && obj[n] && typeof obj[n] === 'object') {
          const v = this.pick(obj[n], keys, undefined)
          if (v !== undefined && v !== null) return v
        }
      }
      return null
    },
    asList (data, keys) {
      if (Array.isArray(data)) return data
      const v = this.unwrap(data, keys)
      return Array.isArray(v) ? v : []
    },
    typeStatsMap () {
      const s = this.summary
      if (!s || typeof s !== 'object') return {}
      const containers = ['byType', 'parts', 'categories', 'types', 'drilldown', 'byCategory', 'perType']
      for (const c of containers) {
        if (s[c] && typeof s[c] === 'object' && !Array.isArray(s[c]) && this.looksLikeTypeMap(s[c])) {
          return s[c]
        }
      }
      // The summary itself may be the per-type map.
      if (this.looksLikeTypeMap(s)) return s
      return {}
    },
    looksLikeTypeMap (obj) {
      return Object.keys(obj || {}).some((k) => {
        const v = obj[k]
        return v && typeof v === 'object' && !Array.isArray(v) &&
          (v.count !== undefined || v.openFindings !== undefined || v.blockers !== undefined ||
           v.warnings !== undefined || v.total !== undefined)
      })
    },
    normCat (r, type) {
      const o = (r && typeof r === 'object') ? r : {}
      return {
        type,
        total: this.numOrZero(this.pick(o, ['count', 'total', 'parts', 'totalParts', 'partCount'], 0)),
        open: this.numOrZero(this.pick(o, ['openFindings', 'open', 'findings'], 0)),
        blockers: this.numOrZero(this.pick(o, ['blockers', 'blocker'], 0)),
        warnings: this.numOrZero(this.pick(o, ['warnings', 'warning'], 0))
      }
    },
    numOrNull (v) {
      if (typeof v === 'number' && !isNaN(v)) return v
      if (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v))) return Number(v)
      return null
    },
    numOrZero (v) {
      const n = this.numOrNull(v)
      return n === null ? 0 : n
    },
    fid (f) { return this.pick(f, ['id', '_id', 'findingId', 'uuid'], '') },
    sev (f) { return this.pick(f, ['severity', 'level', 'sev'], '') },
    issueOf (f) { return this.pick(f, ['issue', 'rule', 'code', 'check', 'kind'], '') },
    typeOf (f) {
      const direct = this.pick(f, ['partType', 'part_type', 'partCategory', 'part_category', 'type', 'category'], '')
      if (direct) return direct
      const part = this.pick(f, ['part'], null)
      return this.pick(part, ['partType', 'part_type', 'type', 'category'], '')
    },
    categoryLabel (f) {
      const type = this.typeOf(f)
      return type ? this.prettyType(type) : 'Unknown'
    },
    statusOf (f) { return this.pick(f, ['status', 'state'], 'open') },
    isOpen (f) { return String(this.statusOf(f)).toLowerCase() === 'open' },
    partId (f) { return this.pick(f, ['partId', 'part_id', 'partID'], this.pick(this.pick(f, ['part'], {}), ['id', '_id'], '')) },
    partRecordName (part) {
      if (!part || typeof part !== 'object') return ''
      const general = this.pick(part, ['general'], {}) || {}
      const name = this.pick(part, ['partName', 'part_name', 'name', 'title', 'model', 'product_name', 'productName'], '') ||
        this.pick(general, ['name', 'title', 'model', 'product_name', 'productName', 'Model'], '')
      if (!name) return ''
      const brand = this.pick(part, ['brand', 'manufacturer'], '') ||
        this.pick(general, ['brand', 'manufacturer', 'Brand'], '')
      const cleanName = String(name).trim()
      const cleanBrand = String(brand || '').trim()
      if (!cleanBrand || cleanName.toLowerCase().indexOf(cleanBrand.toLowerCase()) === 0) return cleanName
      return `${cleanBrand} ${cleanName}`
    },
    partRecordForFinding (f) {
      const embedded = this.pick(f, ['part'], null)
      if (embedded && this.partRecordName(embedded)) return embedded
      const id = String(this.partId(f) || '')
      const type = this.catalogPartType(this.typeOf(f))
      return (this.parts || []).find((part) => {
        if (String(this.partRowId(part) || '') !== id) return false
        const partType = this.catalogPartType(this.partRowType(part))
        return !type || !partType || type === partType
      }) || embedded
    },
    partLabel (f) {
      const direct = this.pick(f, [
        'partName', 'part_name', 'partTitle', 'part_title', 'partModel', 'part_model',
        'productName', 'product_name', 'model'
      ], '')
      if (direct) return direct
      const name = this.partRecordName(this.partRecordForFinding(f))
      if (name) return name
      const id = this.partId(f)
      return `${this.categoryLabel(f)} part${id ? ` #${id}` : ''}`
    },
    fieldPath (f) { return this.pick(f, ['fieldPath', 'field', 'path', 'attribute'], '') },
    curVal (f) {
      return this.pickDeep(f, ['currentValue', 'current_value', 'current', 'currentVal', 'actual', 'actualValue',
        'observed', 'got', 'found', 'existing', 'value'])
    },
    expVal (f) {
      return this.pickDeep(f, ['expectedValue', 'expected_value', 'expected', 'expectedVal', 'standard', 'wanted', 'requirement'])
    },
    sugVal (f) {
      return this.pickDeep(f, ['suggestedValue', 'suggested_value', 'suggestion', 'suggested', 'proposed', 'recommended', 'fix', 'aiValue'])
    },
    isEmptyVal (v) { return v === null || v === undefined || v === '' },
    hasSuggestion (f) {
      return this.truthy(this.pick(f, ['has_suggestion', 'hasSuggestion'], false)) || !this.isEmptyVal(this.sugVal(f))
    },
    isAmazonPriceFinding (f) {
      if (!f) return false
      const field = String(this.fieldPath(f) || '').toLowerCase()
      const issue = String(this.issueOf(f) || '').toLowerCase()
      return field.includes('amzn_price') || issue.includes('amzn_price') ||
        field.includes('amazon_price') || issue.includes('amazon_price')
    },
    isAmazonPriceLookupFailure (f) {
      if (!this.isAmazonPriceFinding(f)) return false
      const values = [
        this.fmtVal(this.curVal(f)),
        this.fmtVal(this.expVal(f)),
        this.evidenceSummary(f),
        this.evidenceFull(f),
        this.issueOf(f)
      ].map((value) => String(value || '').toLowerCase())

      return values.some((value) => {
        return value.includes('could not match') ||
          value.includes('could not catch') ||
          value.includes('could not capture') ||
          value.includes('could not parse') ||
          value.includes('no reusable price') ||
          value.includes('no usable price') ||
          value.includes('parseable usd price') ||
          value.includes('currently unavailable') ||
          value.includes('out of stock')
      })
    },
    findingIssueLabel (f) {
      if (this.isAmazonPriceLookupFailure(f)) return 'Amazon price unavailable'
      if (this.isAmazonPriceFinding(f)) return 'Missing Amazon price'
      return this.prettyLabel(this.issueOf(f) || 'Catalog finding')
    },
    canArchivePriceFinding (f) {
      if (!f || !this.isOpen(f)) return false
      return this.isAmazonPriceLookupFailure(f)
    },
    evidence (f) {
      const value = this.pick(f, ['evidence', 'source', 'sourceUrl', 'reference', 'note', 'sources'], '')
      if (value && typeof value === 'object') {
        return this.pick(value, ['sourceUrl', 'source', 'url', 'note', 'message'], '')
      }
      return value
    },
    evidenceSourceUrl (f) {
      const data = this.evidenceData(f)
      const direct = this.pick(data, ['sourceUrl', 'source_url', 'url', 'href', 'referenceUrl', 'reference_url'], '')
      if (direct) return direct
      const sources = this.pick(data, ['sources', 'references'], null)
      if (Array.isArray(sources)) {
        const found = sources.find((source) => {
          return this.isUrl(typeof source === 'object' ? this.pick(source, ['url', 'href', 'sourceUrl'], '') : source)
        })
        if (found) return typeof found === 'object' ? this.pick(found, ['url', 'href', 'sourceUrl'], '') : found
      }
      const raw = this.evidence(f)
      return this.isUrl(raw) ? raw : ''
    },
    evidenceSummary (f) {
      if (this.hasSuggestion(f)) return 'Suggested'
      const data = this.evidenceData(f)
      const tracking = this.trackingOf(f) || {}
      const raw = this.evidence(f)
      const mode = this.pick(tracking, ['mode'], '')
      const action = this.pick(tracking, ['action'], '')
      const source = this.pick(data, ['source', 'method', 'rule', 'kind', 'note', 'message', 'reason'], '')

      if (mode || action) {
        const cleanMode = this.prettyLabel(mode)
        const cleanAction = this.prettyLabel(action)
        if (String(action).toLowerCase() === 'apply') return 'Suggestion applied'
        if (String(action).toLowerCase() === 'research') {
          return this.truncate(cleanMode ? `${cleanMode} research` : 'Research', 56)
        }
        return this.truncate([cleanMode, cleanAction].filter(Boolean).join(' '), 56)
      }
      if (source) return this.truncate(this.prettyLabel(source), 56)
      if (raw === 'rule') return 'Rule check'
      if (typeof raw === 'string' && raw && !this.safeJson(raw) && !this.isUrl(raw)) return this.truncate(raw, 56)
      if (this.evidenceSourceUrl(f)) return 'Source'
      return 'Open details'
    },
    evidenceFull (f) {
      const data = this.evidenceData(f)
      if (data && typeof data === 'object') {
        try { return JSON.stringify(data, null, 2) } catch (e) { return this.fmtVal(data) }
      }
      const raw = this.evidence(f)
      if (raw === null || raw === undefined || raw === '') return ''
      const parsed = typeof raw === 'string' ? this.safeJson(raw) : null
      if (parsed) {
        try { return JSON.stringify(parsed, null, 2) } catch (e) { return this.fmtVal(parsed) }
      }
      return this.fmtVal(raw)
    },
    findingEvidenceRows (f) {
      const data = this.evidenceData(f) || {}
      const tracking = this.trackingOf(f) || {}
      const aiResponse = this.aiResponseData(f)
      const rows = []
      const add = (label, value) => {
        if (value === null || value === undefined || value === '') return
        rows.push({ label, value: this.fmtVal(value) })
      }

      add('Suggestion', this.hasSuggestion(f) ? (this.sugVal(f) || 'Available') : '')
      add('Source', this.pick(data, ['source', 'method', 'rule', 'kind'], ''))
      add('Action', this.pick(tracking, ['action'], ''))
      add('Mode', this.pick(tracking, ['mode'], ''))
      add('Status', this.pick(tracking, ['status', 'state'], ''))
      add('Reason', this.pick(tracking, ['reason'], ''))
      add('Before', this.actionChangeValue(this.pick(tracking, ['before'], null)))
      add('After', this.actionChangeValue(this.pick(tracking, ['after'], null)))
      add('AI selected fix', this.pick(aiResponse, ['selected_fix', 'selectedFix'], ''))
      return rows
    },
    aiResponseData (f) {
      const data = this.evidenceData(f)
      if (!data || typeof data !== 'object') return null
      const aiResponse = this.pick(data, ['ai_response', 'aiResponse'], null)
      if (typeof aiResponse === 'string') return this.safeJson(aiResponse) || { raw_output_text: aiResponse }
      return aiResponse && typeof aiResponse === 'object' ? aiResponse : null
    },
    aiResponseRows (f) {
      const aiResponse = this.aiResponseData(f)
      if (!aiResponse) return []
      const fields = [
        { key: 'raw_output_text', label: 'Raw output', aliases: ['raw_output_text', 'rawOutputText'] },
        { key: 'selected_fix', label: 'Selected fix', aliases: ['selected_fix', 'selectedFix'] },
        { key: 'fixes', label: 'Fixes', aliases: ['fixes'] },
        { key: 'issues', label: 'Issues', aliases: ['issues'] }
      ]
      return fields.map((field) => {
        const value = this.pick(aiResponse, field.aliases, null)
        if (value === null || value === undefined || value === '') return null
        return {
          key: field.key,
          label: field.label,
          value: this.fmtVal(value)
        }
      }).filter(Boolean)
    },
    evidenceData (f) {
      const direct = this.pick(f, ['evidence_data', 'evidenceData'], null)
      if (typeof direct === 'string') return this.safeJson(direct) || null
      if (direct) return direct
      const evidence = this.pick(f, ['evidence'], null)
      if (evidence && typeof evidence === 'object') return evidence
      if (typeof evidence === 'string') {
        return this.safeJson(evidence) || null
      }
      return null
    },
    trackingOf (f) {
      const direct = this.pick(f, ['tracking'], null)
      if (typeof direct === 'string') return this.safeJson(direct) || null
      if (direct) return direct
      const evidenceData = this.evidenceData(f)
      if (evidenceData && typeof evidenceData === 'object') {
        const nested = this.pick(evidenceData, ['tracking', 'lastAction', 'last_action'], null)
        if (typeof nested === 'string') return this.safeJson(nested) || null
        return nested
      }
      return null
    },
    findingActionState (f) {
      const id = this.fid(f)
      if (this.busy[id]) {
        return {
          label: this.busy[id] === true ? 'Working...' : this.prettyLabel(this.busy[id]) + ' running...',
          tone: 'info',
          detail: ''
        }
      }
      if (this.hasSuggestion(f)) {
        return {
          label: 'Suggested',
          tone: 'ok',
          detail: this.sugVal(f) ? this.truncate(this.fmtVal(this.sugVal(f)), 44) : ''
        }
      }
      if (this.actionResults[id]) return this.actionResults[id]
      return this.actionResultFromTracking(this.trackingOf(f))
    },
    actionResultFromResponse (data, action) {
      const payload = data || {}
      const tracking = this.pick(payload, ['tracking'], null)
      const base = Object.assign({}, tracking || {}, payload)
      const result = this.actionResultFromTracking(base)
      if (result.label) return result
      if (action === 'apply') return { label: 'Apply completed', tone: 'ok', detail: '' }
      if (action === 'archive') return { label: 'Part archived', tone: 'ok', detail: '' }
      return { label: 'Research completed', tone: 'ok', detail: '' }
    },
    actionResultFromTracking (tracking) {
      if (!tracking || typeof tracking !== 'object') {
        return { label: '', tone: '', detail: '' }
      }

      const helped = this.pick(tracking, ['helped'], null)
      const suggested = this.pick(tracking, ['suggested'], null)
      const applied = this.pick(tracking, ['applied'], null)
      const archived = this.pick(tracking, ['archived', 'partArchived', 'part_archived'], null)
      const resolved = this.pick(tracking, ['resolved'], null)
      const reason = this.pick(tracking, ['reason'], '')
      const reasonKey = String(reason || '').replace(/_/g, '-')
      const status = String(this.pick(tracking, ['status', 'state'], '') || '').toLowerCase()
      const detail = this.actionChangeDetail(tracking) || (reason ? this.prettyLabel(reason) : '')

      if (this.truthy(archived)) {
        return { label: 'Part archived', tone: 'ok', detail }
      }
      if (this.truthy(applied)) {
        return { label: 'Fix applied', tone: 'ok', detail }
      }
      if (this.truthy(resolved)) {
        return { label: 'Finding no longer exists', tone: 'ok', detail }
      }
      if (this.truthy(helped) && this.truthy(suggested)) {
        return { label: 'Suggestion found', tone: 'ok', detail }
      }
      if (this.falsey(helped) && reasonKey === 'no-rule-suggestion') {
        return { label: 'No rule fix found', tone: 'warn', detail: '' }
      }
      if (this.falsey(helped) && status === 'failed') {
        return { label: 'Research failed', tone: 'bad', detail }
      }
      if (status === 'failed') {
        return { label: 'Research failed', tone: 'bad', detail }
      }
      if (this.falsey(helped)) {
        return { label: 'No suggestion found', tone: 'warn', detail }
      }
      if (this.truthy(helped)) {
        return { label: 'Research helped', tone: 'ok', detail }
      }
      return { label: detail, tone: detail ? 'info' : '', detail: '' }
    },
    truthy (value) {
      return value === true || value === 'true' || value === 1 || value === '1'
    },
    falsey (value) {
      return value === false || value === 'false' || value === 0 || value === '0'
    },
    actionChangeDetail (tracking) {
      const before = this.pick(tracking, ['before'], null)
      const after = this.pick(tracking, ['after'], null)
      if (before === null && after === null) return ''

      const beforeValue = this.actionChangeValue(before)
      const afterValue = this.actionChangeValue(after)
      if (!beforeValue && !afterValue) return ''
      return `${beforeValue || 'empty'} → ${afterValue || 'empty'}`
    },
    actionChangeValue (value) {
      if (value === null || value === undefined) return ''
      if (typeof value !== 'object') return this.truncate(value, 26)
      const picked = this.pick(value, ['value', 'suggested_value', 'suggestedValue', 'current_value', 'currentValue', 'after', 'to'], null)
      if (picked !== null && picked !== undefined) return this.truncate(picked, 26)
      return this.truncate(this.fmtVal(value), 26)
    },
    safeJson (value) {
      try {
        return JSON.parse(value)
      } catch (e) {
        return null
      }
    },

    partRowId (p) { return this.pick(p, ['id', '_id', 'partId', 'part_id'], '') },
    partRowName (p) { return this.partRecordName(p) || `${this.prettyType(this.partRowType(p))} part #${this.partRowId(p)}` },
    partRowType (p) { return this.pick(p, ['type', 'partType', 'part_type', 'category'], '') },
    partBlockers (p) { return this.pick(p, ['blockers', 'blocker', 'blockerCount'], 0) },
    partWarnings (p) { return this.pick(p, ['warnings', 'warning', 'warningCount'], 0) },
    topIssues (p) {
      let iss = this.pick(p, ['topIssues', 'issues', 'issueList', 'top'], [])
      if (typeof iss === 'string') iss = iss.split(',').map((x) => x.trim()).filter(Boolean)
      if (!Array.isArray(iss)) return []
      return iss.slice(0, 4).map((x) => (typeof x === 'object' ? this.pick(x, ['issue', 'rule', 'name', 'code'], '') : x))
    },
    mergePartDetail (base, detail) {
      const part = Object.assign({}, base || {}, detail || {})
      part.id = this.pick(detail, ['id'], this.pick(base, ['id'], ''))
      part.type = this.catalogPartType(this.pick(detail, ['type'], this.pick(base, ['type'], '')))
      part.name = this.partDisplayName(part)
      part.asin = this.partAsin(part)
      return part
    },
    partModelFromFinding (f) {
      const part = this.pick(f, ['part'], null) || {}
      const id = this.partId(f)
      const fallbackType = this.filters.type && this.filters.type !== 'all' ? this.filters.type : ''
      const type = this.typeOf(f) || this.pick(part, ['type', 'partType', 'category'], fallbackType)
      return {
        id,
        type,
        name: this.partLabel(f),
        asin: this.pick(part, ['asin'], this.pick(f, ['asin'], '')),
        row: part,
        source: f
      }
    },
    partModelFromRow (p) {
      const fallbackType = this.filters.type && this.filters.type !== 'all' ? this.filters.type : ''
      return {
        id: this.partRowId(p),
        type: this.partRowType(p) || fallbackType,
        name: this.partRowName(p),
        asin: this.pick(p, ['asin'], ''),
        row: p,
        source: p
      }
    },
    partDisplayName (part) {
      const general = this.pick(part, ['general'], {}) || {}
      return this.pick(general, ['name', 'title', 'model', 'product_name', 'productName'], '') ||
        this.pick(part, ['name', 'title', 'model', 'product_name', 'productName'], '') ||
        (this.partAsin(part) ? `ASIN ${this.partAsin(part)}` : `Part #${this.pick(part, ['id'], '?')}`)
    },
    partAsin (part) {
      const general = this.pick(part, ['general'], {}) || {}
      return this.pick(part, ['asin', 'ASIN'], '') || this.pick(general, ['asin', 'ASIN'], '')
    },
    partImage (part) {
      const general = this.pick(part, ['general'], {}) || {}
      const direct = this.pick(part, ['image', 'img', 'imgUrl', 'imageUrl', 'mainImg', 'thumbnail'], '')
      const fromGeneral = this.pick(general, ['image', 'img', 'imgUrl', 'imageUrl', 'mainImg', 'thumbnail'], '')
      return this.imageValue(direct) || this.imageValue(fromGeneral)
    },
    imageValue (value) {
      if (!value) return ''
      if (typeof value === 'string') return value
      if (Array.isArray(value)) return this.imageValue(value[0])
      if (typeof value === 'object') {
        return this.pick(value, ['sample', 'main', 'url', 'src', 'thumbnail', 'imageUrl', 'mainImg'], '')
      }
      return ''
    },
    partPriceText (part) {
      const price = this.pick(part, ['price'], null)
      const direct = this.pick(part, ['amzn_price', 'priceValue', 'price_value'], '')
      const value = this.pick(price, ['amzn_price', 'current', 'amount', 'value', 'price'], direct)
      if (value === null || value === undefined || value === '') return ''
      if (typeof value === 'number') return `$${value.toFixed(2)}`
      return String(value)
    },
    partAmazonUrl (part) {
      const asin = this.partAsin(part)
      return amazonProductUrl(asin)
    },
    partSummaryRows (part) {
      const rows = []
      const add = (label, value) => {
        if (value === null || value === undefined || value === '') return
        rows.push({ label, value: this.fmtVal(value) })
      }
      add('ASIN', this.partAsin(part))
      add('Price', this.partPriceText(part))
      add('Price type', this.pick(part, ['price_type', 'priceType'], ''))
      add('Release date', this.partReleaseValue(part, ['release_date', 'releaseDate']))
      add('Amazon', this.partAmazonUrl(part) ? 'Available' : '')
      return rows
    },
    partReleaseRows (part) {
      const rows = []
      const add = (key, label, keys, urlKeys) => {
        const value = this.partReleaseValue(part, keys)
        if (value === null || value === undefined || value === '' || value === 'N/A') return
        const url = urlKeys ? this.partReleaseValue(part, urlKeys) : ''
        rows.push({ key, label, value: this.fmtVal(value), url })
      }
      add('date', 'Release date', ['release_date', 'releaseDate'])
      add('precision', 'Precision', ['release_date_precision', 'releaseDatePrecision'])
      add('confidence', 'Confidence', ['release_date_confidence', 'releaseDateConfidence'])
      add('source_name', 'Source', ['release_date_source_name', 'releaseDateSourceName'], ['release_date_source_url', 'releaseDateSourceUrl'])
      add('source_url', 'Source URL', ['release_date_source_url', 'releaseDateSourceUrl'], ['release_date_source_url', 'releaseDateSourceUrl'])
      add('note', 'Research note', ['release_date_note', 'releaseDateNote'])
      add('checked_at', 'Checked at', ['release_date_checked_at', 'releaseDateCheckedAt'])
      return rows
    },
    partReleaseValue (part, keys) {
      const sources = [
        part,
        this.pick(part, ['general'], null),
        this.pick(part, ['specs'], null),
        this.pick(part, ['compat'], null),
        this.pick(part, ['price'], null)
      ]
      for (const source of sources) {
        const value = this.pick(source, keys, '')
        if (value !== null && value !== undefined && value !== '') return value
      }
      return ''
    },
    partDetailSections (part) {
      const candidates = [
        { key: 'general', label: 'General' },
        { key: 'specs', label: 'Specifications' },
        { key: 'compat', label: 'Compatibility' },
        { key: 'sockets', label: 'Socket details' },
        { key: 'price', label: 'Pricing' }
      ]
      return candidates
        .map((section) => {
          return Object.assign({}, section, { rows: this.partSectionRows(part, section.key) })
        })
        .filter((section) => section.rows.length > 0)
    },
    partSectionRows (part, key) {
      const source = this.pick(part, [key], null)
      if (!source || typeof source !== 'object' || Array.isArray(source)) return []
      return Object.keys(source)
        .filter((field) => {
          const value = source[field]
          if (/^release(_date)?|^releaseDate/.test(field)) return false
          return value !== null && value !== undefined && value !== '' && value !== 'N/A'
        })
        .slice(0, 18)
        .map((field) => {
          return {
            key: field,
            label: this.prettyLabel(field),
            value: this.truncate(this.fmtVal(source[field]), 180)
          }
        })
    },
    selectedPartSeverityCount (severity) {
      return this.selectedPartFindings.filter((f) => {
        return String(this.sev(f)).toLowerCase() === severity
      }).length
    },
    catalogPartType (type) {
      const t = String(type || '').toLowerCase()
      if (t === 'processor') return 'cpu'
      if (t === 'graphic' || t === 'graphics') return 'gpu'
      if (t === 'power supply' || t === 'power_supply') return 'psu'
      return t || 'all'
    },
    adminListType (type) {
      const t = this.catalogPartType(type)
      if (t === 'gpu') return 'graphics'
      return t
    },

    auditId (a) { return this.pick(a, ['id', '_id', 'auditId'], Math.random().toString(36).slice(2)) },
    auditWhen (a) { return this.pick(a, ['appliedAt', 'createdAt', 'at', 'timestamp', 'date'], null) },
    auditPart (a) {
      const direct = this.pick(a, ['partName', 'name', 'title'], '')
      if (direct) return direct
      return `#${this.pick(a, ['partId', 'part_id'], '?')}`
    },
    auditField (a) { return this.pick(a, ['fieldPath', 'field', 'path'], '') },
    auditOld (a) { return this.pick(a, ['oldValue', 'from', 'previous', 'before'], null) },
    auditNew (a) { return this.pick(a, ['newValue', 'to', 'value', 'after'], null) },
    auditSource (a) { return this.pick(a, ['source', 'origin', 'job', 'method'], '') },
    auditBy (a) { return this.pick(a, ['by', 'user', 'actor', 'appliedBy'], '') },
    runId (run) { return this.pick(run, ['id', '_id', 'runId'], '') },
    runJobKey (run) { return this.pick(run, ['jobKey', 'job', 'key'], this.trackedJobKey || '') },
    runStatus (run) { return this.pick(run, ['status', 'state'], 'queued') },
    runStartedAt (run) { return this.pick(run, ['startedAt', 'createdAt', 'queuedAt', 'startTime'], '') },
    runEndedAt (run) { return this.pick(run, ['endedAt', 'finishedAt', 'completedAt', 'endTime'], '') },
    isRunActive (run) {
      const status = String(this.runStatus(run)).toLowerCase()
      return ['starting', 'queued', 'pending', 'running', 'active'].indexOf(status) >= 0
    },
    runDuration (run) {
      const start = new Date(this.runStartedAt(run))
      if (isNaN(start)) return 'Waiting for run details'
      const endValue = this.runEndedAt(run)
      const end = endValue ? new Date(endValue) : new Date()
      if (isNaN(end)) return 'In progress'
      const seconds = Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000))
      if (seconds < 60) return `${seconds}s elapsed`
      const minutes = Math.floor(seconds / 60)
      const rem = seconds % 60
      return `${minutes}m ${rem}s elapsed`
    },
    runProgress (run) {
      if (!run) return 0
      if (!this.isRunActive(run)) return 100
      const flat = this.flattenStats(this.pick(run, ['stats', 'result'], {}))
      const processed = this.firstStatNumber(flat, ['processed', 'scanned', 'checked', 'fixed', 'repaired', 'enriched', 'requested'])
      const total = this.firstStatNumber(flat, ['total', 'limit', 'count', 'requested'])
      if (processed !== null && total && total > 0) {
        return Math.max(6, Math.min(96, Math.round((processed * 100) / total)))
      }
      return 42
    },
    firstStatNumber (flat, names) {
      for (const name of names) {
        const hit = flat.find((stat) => this.statLeaf(stat.key).toLowerCase() === name)
        if (hit && typeof hit.value === 'number' && !isNaN(hit.value)) return hit.value
      }
      return null
    },
    runSummaryChips (run) {
      if (!run) return []
      return this.flattenStats(this.pick(run, ['stats', 'result'], {}))
        .filter((stat) => typeof stat.value !== 'object')
        .filter((stat) => !['researchnote', 'note'].includes(this.statLeaf(stat.key).toLowerCase()))
        .slice(0, 5)
        .map((stat) => {
          return {
            key: stat.key,
            label: this.prettyLabel(this.statLeaf(stat.key)),
            value: typeof stat.value === 'boolean' ? (stat.value ? 'yes' : 'no') : stat.value,
            tone: this.statTone(stat.key, stat.value)
          }
        })
    },
    flattenStats (stats, prefix) {
      if (!stats || typeof stats !== 'object') return []
      let out = []
      Object.keys(stats).forEach((key) => {
        const value = stats[key]
        const full = prefix ? `${prefix}.${key}` : key
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          out = out.concat(this.flattenStats(value, full))
        } else {
          out.push({ key: full, value })
        }
      })
      return out
    },
    statLeaf (key) {
      const parts = String(key || '').split('.')
      return parts[parts.length - 1] || key
    },
    statTone (key, value) {
      const leaf = this.statLeaf(key).toLowerCase()
      if (['failed', 'errors', 'invalid', 'blockers', 'dead'].some((k) => leaf.includes(k))) return 'bad'
      if (['warning', 'warnings', 'skipped', 'duplicates'].some((k) => leaf.includes(k))) return 'warn'
      if (typeof value === 'number' && value === 0) return 'muted'
      return 'ok'
    },

    // ---- formatting ----
    severityClass (sev) {
      const s = String(sev || '').toLowerCase()
      if (s === 'blocker' || s === 'critical' || s === 'error') return 'sev-blocker'
      if (s === 'warning' || s === 'warn') return 'sev-warning'
      return 'sev-info'
    },
    prettyType (t) {
      const s = String(t || '').toLowerCase()
      const map = { cpu: 'CPU', gpu: 'GPU', ram: 'RAM', psu: 'PSU', motherboard: 'Motherboard', storage: 'Storage', case: 'Case', all: 'All' }
      return map[s] || this.prettyLabel(t)
    },
    prettyLabel (value) {
      return String(value || '')
        .replace(/[._-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    },
    fmtVal (v) {
      if (v === null || v === undefined || v === '') return '—'
      if (typeof v === 'boolean') return v ? 'true' : 'false'
      if (typeof v === 'object') {
        try { return JSON.stringify(v) } catch (e) { return String(v) }
      }
      return String(v)
    },
    isUrl (v) { return typeof v === 'string' && /^https?:\/\//i.test(v) },
    truncate (v, n) {
      const s = this.fmtVal(v)
      if (s === '—') return ''
      return s.length > n ? s.slice(0, n - 1) + '…' : s
    },
    fmtTime (t) {
      if (!t) return '--'
      const d = new Date(t)
      return isNaN(d) ? '--' : d.toLocaleString()
    },
    fmtRelative (t) {
      if (!t) return '--'
      const d = new Date(t)
      if (isNaN(d)) return '--'
      const s = Math.round((Date.now() - d.getTime()) / 1000)
      if (s < 0) return this.fmtTime(t)
      if (s < 45) return 'just now'
      const m = Math.round(s / 60)
      if (m < 60) return `${m}m ago`
      const h = Math.round(m / 60)
      if (h < 24) return `${h}h ago`
      const days = Math.round(h / 24)
      if (days < 7) return `${days}d ago`
      return this.fmtTime(t)
    },
    errMsg (e) {
      return (e.response && e.response.data && e.response.data.message) || e.message || 'Request failed'
    }
  }
}
</script>

<style scoped>
.health-page {
  padding: 4px 8px 48px;
  color: #cfd2da;
}


.label {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: 1px solid rgba(67, 72, 87, 0.9);
}

.label h4 {
  margin: 0 0 6px;
  color: #d8dde8;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.intro {
  max-width: 760px;
  margin: 0.6rem 0 1.1rem;
  color: #a4acbc;
  font-size: 14px;
  line-height: 1.55;
}
.qa-schedule {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin: -0.4rem 0 1.1rem;
  color: #a4acbc;
  font-size: 13px;
}
.qa-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #6b7385;
}
.qa-dot.is-on { background: #1bc98e; box-shadow: 0 0 0 3px rgba(27, 201, 142, 0.15); }

.inline-link {
  color: #1fb2e8;
  text-decoration: none;
}

.inline-link:hover {
  text-decoration: underline;
}

.alert,
.notice {
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
}

.alert {
  border: 1px solid rgba(232, 79, 106, 0.5);
  background: rgba(90, 34, 48, 0.48);
  color: #f3c6cf;
}

.notice {
  border: 1px solid rgba(25, 151, 198, 0.5);
  background: rgba(20, 54, 70, 0.5);
  color: #aee0f3;
}

.job-progress-card {
  margin: 12px 0 16px;
  padding: 13px 14px;
  border: 1px solid rgba(67, 72, 87, 0.86);
  border-radius: 8px;
  background:
    radial-gradient(circle at 18% 0%, rgba(25, 151, 198, 0.12), transparent 36%),
    rgba(10, 14, 22, 0.78);
}

.job-progress-card.is-running {
  border-color: rgba(25, 151, 198, 0.5);
}

.job-progress-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.job-progress-head h5 {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 2px 0 0;
  color: #dce1eb;
  font-size: 15px;
  font-weight: 800;
}

.run-status {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0 8px;
  border: 1px solid rgba(117, 124, 146, 0.34);
  border-radius: 999px;
  color: #aeb5c5;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.run-status.st-running,
.run-status.st-queued {
  border-color: rgba(25, 151, 198, 0.5);
  background: rgba(25, 151, 198, 0.12);
  color: #7bccee;
}

.run-status.st-success {
  border-color: rgba(27, 201, 142, 0.42);
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
}

.run-status.st-failed,
.run-status.st-error {
  border-color: rgba(232, 79, 106, 0.44);
  background: rgba(232, 79, 106, 0.12);
  color: #f0a1af;
}

.progress-track {
  position: relative;
  height: 5px;
  margin-top: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(43, 51, 67, 0.78);
}

.progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, rgba(25, 151, 198, 0.78), rgba(27, 201, 142, 0.72));
  transition: width 0.45s ease;
}

.job-progress-card.is-running .progress-fill {
  background:
    linear-gradient(90deg, rgba(25, 151, 198, 0.42), rgba(27, 201, 142, 0.76), rgba(25, 151, 198, 0.42));
  background-size: 180% 100%;
  animation: progress-sheen 1.8s linear infinite;
}

@keyframes progress-sheen {
  from { background-position: 180% 0; }
  to { background-position: -180% 0; }
}

.job-progress-meta {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 8px;
  color: #8f98aa;
  font-size: 12px;
}

.job-progress-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 10px;
}

.job-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border: 1px solid rgba(67, 72, 87, 0.74);
  border-radius: 999px;
  background: rgba(20, 25, 35, 0.58);
  color: #aeb5c5;
  font-size: 11px;
}

.job-chip strong {
  color: #dce1eb;
}

.job-chip.tone-ok strong {
  color: #89dfbd;
}

.job-chip.tone-warn strong {
  color: #e0c07e;
}

.job-chip.tone-bad strong {
  color: #f0a1af;
}

.job-chip.tone-muted {
  opacity: 0.58;
}

/* KPIs */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin: 18px 0 8px;
}

.kpi-card {
  display: grid;
  gap: 6px;
  padding: 16px;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.08), transparent 38%),
    rgba(14, 18, 27, 0.78);
}

.kpi-label {
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.kpi-value {
  color: #eaf0f7;
  font-size: 30px;
  font-weight: 800;
  line-height: 1;
}

.kpi-card.tone-bad {
  border-color: rgba(232, 79, 106, 0.4);
}

.kpi-card.tone-bad .kpi-value {
  color: #f0a1af;
}

.kpi-card.tone-warn {
  border-color: rgba(224, 192, 126, 0.36);
}

.kpi-card.tone-warn .kpi-value {
  color: #e0c07e;
}

/* Actions */
.actions-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 18px 0 6px;
}

.gc-act {
  height: 36px;
  padding: 0 14px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 7px;
  background: rgba(20, 25, 35, 0.7);
  color: #d7dce8;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 700;
  transition: border-color 0.16s ease, background 0.16s ease, color 0.16s ease;
}

.gc-act:hover:not(:disabled) {
  border-color: rgba(25, 151, 198, 0.7);
  background: rgba(25, 151, 198, 0.12);
  color: #eaf7fc;
}

.gc-act.primary {
  border-color: rgba(25, 151, 198, 0.8);
  background: rgba(25, 151, 198, 0.14);
  color: #1fb2e8;
}

.amazon-price-run-control {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  height: 36px;
  min-height: 36px;
  padding: 3px 4px 3px 10px;
  border: 1px solid rgba(240, 170, 64, 0.25);
  border-radius: 8px;
  background: rgba(240, 170, 64, 0.045);
}

.amazon-price-run-control label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0;
}

.amazon-price-run-control label span {
  color: #b6a686;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}

.amazon-price-run-control input {
  width: 76px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid rgba(104, 111, 129, 0.72);
  border-radius: 6px;
  outline: none;
  background: rgba(7, 11, 18, 0.9);
  color: #eef2f8;
  font: inherit;
  font-size: 12px;
}

.amazon-price-run-control input:focus {
  border-color: rgba(240, 170, 64, 0.76);
  box-shadow: 0 0 0 2px rgba(240, 170, 64, 0.1);
}

.amazon-price-scope-note {
  color: #9299a8;
  font-size: 10px;
  white-space: nowrap;
}

.amazon-price-run-control .gc-act {
  height: 28px;
  margin: 0;
}

.gc-act.amazon-price-action {
  border-color: rgba(240, 170, 64, 0.52);
  background: rgba(240, 170, 64, 0.1);
  color: #efc071;
}

.gc-act.amazon-price-action:hover:not(:disabled) {
  border-color: rgba(240, 170, 64, 0.82);
  background: rgba(240, 170, 64, 0.16);
  color: #ffe0a8;
}

.gc-act.archive {
  border-color: rgba(224, 192, 126, 0.42);
  background: rgba(224, 192, 126, 0.08);
  color: #e0c07e;
}

.gc-act.archive:hover:not(:disabled) {
  border-color: rgba(224, 192, 126, 0.72);
  background: rgba(224, 192, 126, 0.14);
  color: #f2dba5;
}

.gc-act.subtle {
  color: #9aa3b4;
}

.gc-act.danger-soft {
  border-color: rgba(232, 79, 106, 0.36);
  background: rgba(232, 79, 106, 0.08);
  color: #f0a1af;
}

.gc-act.danger-soft:hover:not(:disabled) {
  border-color: rgba(232, 79, 106, 0.68);
  background: rgba(232, 79, 106, 0.14);
  color: #ffd4dc;
}

.gc-act:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Section heads */
.section-head {
  margin: 26px 0 10px;
}

.section-head h5 {
  margin: 2px 0 0;
  color: #d8dde8;
  font-size: 15px;
  font-weight: 700;
}

/* Category drilldown */
.cat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.cat-tile {
  display: grid;
  gap: 6px;
  padding: 12px;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background: rgba(14, 18, 27, 0.7);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.16s ease, background 0.16s ease;
}

.cat-tile:hover,
.cat-tile.is-active {
  border-color: rgba(25, 151, 198, 0.62);
  background: rgba(16, 21, 31, 0.86);
}

.cat-name {
  color: #dce1eb;
  font-size: 14px;
  font-weight: 700;
}

.cat-total {
  color: #8f98aa;
  font-size: 12px;
}

.cat-pills {
  display: flex;
  gap: 6px;
}

.mini-pill,
.count-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border: 1px solid rgba(117, 124, 146, 0.3);
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 800;
}

.mini-pill.tone-bad,
.count-pill.tone-bad {
  border-color: rgba(232, 79, 106, 0.4);
  background: rgba(232, 79, 106, 0.12);
  color: #f0a1af;
}

.mini-pill.tone-warn,
.count-pill.tone-warn {
  border-color: rgba(224, 192, 126, 0.36);
  background: rgba(224, 192, 126, 0.12);
  color: #e0c07e;
}

.mini-pill.dim,
.count-pill.dim {
  opacity: 0.4;
}

.cat-empty {
  grid-column: 1 / -1;
  padding: 14px;
  color: #6f788b;
  font-size: 13px;
}

/* Tabs + filters */
.tabs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin: 28px 0 12px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
}

.tabs {
  display: flex;
  gap: 4px;
}

.tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #9aa3b4;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
}

.tab:hover {
  color: #d7dce8;
}

.tab.active {
  color: #1fb2e8;
  border-bottom-color: #1fb2e8;
}

.tab-count {
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(58, 63, 77, 0.6);
  color: #aeb5c5;
  font-size: 10.5px;
  font-weight: 800;
}

.filters,
.audit-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.hfilter {
  height: 31px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(10, 13, 20, 0.56);
  color: #cfd2da;
  padding: 0 9px;
  font-size: 12px;
  outline: none;
}

.hfilter.grow {
  flex: 1;
  min-width: 220px;
}

.hfilter:focus {
  border-color: rgba(25, 151, 198, 0.85);
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.12);
}

.audit-filters {
  margin: 4px 0 12px;
}

.btn-refresh {
  min-width: 84px;
  height: 31px;
  border: 1px solid rgba(25, 151, 198, 0.8);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.1);
  color: #1fb2e8;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  cursor: pointer;
}

.btn-refresh:hover {
  border-color: #1fb2e8;
  background: rgba(25, 151, 198, 0.2);
  color: #eaf7fc;
}

.focus-banner {
  margin-bottom: 10px;
  padding: 8px 12px;
  border: 1px solid rgba(25, 151, 198, 0.36);
  border-radius: 7px;
  background: rgba(25, 151, 198, 0.08);
  color: #bfe6f5;
  font-size: 13px;
}

.focus-clear {
  margin-left: 10px;
  color: #1fb2e8;
  cursor: pointer;
  font-weight: 700;
}

/* Tables */
.catalog-table-section {
  min-height: 160px;
}

.table-wrap {
  overflow-x: auto;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background: rgba(9, 13, 20, 0.42);
}

.h-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.h-table th {
  padding: 10px 11px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
  color: #7f899b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.h-table td {
  padding: 10px 11px;
  border-bottom: 1px solid rgba(47, 51, 61, 0.84);
  color: #cfd2da;
  vertical-align: top;
}

.h-table tbody tr:hover {
  background: rgba(25, 151, 198, 0.05);
}

.h-table tbody tr.finding-row {
  cursor: pointer;
}

.h-table tbody tr.finding-row:hover {
  background: rgba(25, 151, 198, 0.075);
}

.sev-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.sev-blocker {
  border: 1px solid rgba(232, 79, 106, 0.4);
  background: rgba(232, 79, 106, 0.14);
  color: #f0a1af;
}

.sev-warning {
  border: 1px solid rgba(224, 192, 126, 0.36);
  background: rgba(224, 192, 126, 0.14);
  color: #e0c07e;
}

.sev-info {
  border: 1px solid rgba(117, 124, 146, 0.3);
  background: rgba(58, 63, 77, 0.5);
  color: #aeb5c5;
}

.finding-classification {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.finding-classification .sev-pill {
  padding-right: 7px;
  padding-left: 7px;
  font-size: 9px;
}

.part-link,
.part-name {
  display: block;
  color: #dce1eb;
  font-weight: 600;
}

.part-link {
  color: #7bccee;
  cursor: pointer;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.part-link:hover {
  text-decoration: underline;
}

.type-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  min-width: 42px;
  padding: 0 8px;
  border: 1px solid rgba(25, 151, 198, 0.32);
  border-radius: 999px;
  background: rgba(25, 151, 198, 0.08);
  color: #7bccee;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.muted-sub {
  display: block;
  margin-top: 2px;
  color: #7f899b;
  font-size: 11px;
}

.fieldpath {
  display: inline-block;
  max-width: 100%;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(58, 63, 77, 0.4);
  color: #b8c0d0;
  font-size: 11.5px;
  overflow-wrap: normal;
  white-space: nowrap;
}

.price-capture-chip {
  display: block;
  width: fit-content;
  margin-top: 5px;
  padding: 2px 7px;
  border: 1px solid rgba(240, 170, 64, 0.42);
  border-radius: 999px;
  background: rgba(240, 170, 64, 0.09);
  color: #efc071;
  font-size: 9.5px;
  font-weight: 800;
  line-height: 1.2;
  max-width: 100%;
  white-space: normal;
}

.val {
  display: block;
  min-width: 0;
  max-width: 160px;
  font-family: monospace;
  font-size: 11.5px;
  word-break: break-word;
}

.val.cur { color: #c9ced9; }

.val-empty {
  color: #7f899b;
  font-family: inherit;
  font-style: italic;
}
.val.exp { color: #89dfbd; }
.val.sug { color: #aab5ff; }
.val.old { color: #c08a93; }
.val.new { color: #89dfbd; }

.change-cell {
  display: flex;
  align-items: center;
  gap: 7px;
}

.change-cell .arrow {
  color: #6f788b;
}

.evidence {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  color: #9aa3b4;
  font-size: 12px;
}

.evidence-summary {
  display: inline-flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  min-height: 22px;
  padding: 2px 9px;
  border: 1px solid rgba(67, 72, 87, 0.78);
  border-radius: 999px;
  background: rgba(14, 18, 27, 0.62);
  color: #b9c2d1;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.02em;
  line-height: 1.2;
  text-decoration: none;
}

.evidence-summary,
.evidence-summary span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.evidence a.evidence-summary {
  border-color: rgba(25, 151, 198, 0.42);
  background: rgba(25, 151, 198, 0.08);
  color: #1fb2e8;
}

.evidence a.evidence-summary:hover {
  border-color: rgba(25, 151, 198, 0.74);
  background: rgba(25, 151, 198, 0.14);
  color: #9bdff5;
  text-decoration: none;
}

.action-state {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  max-width: 240px;
  min-height: 28px;
  padding: 4px 9px;
  border: 1px solid rgba(117, 124, 146, 0.32);
  border-radius: 999px;
  background: rgba(58, 63, 77, 0.3);
  color: #aeb5c5;
  font-size: 10.5px;
  font-weight: 800;
  line-height: 1.15;
}

.action-state strong {
  color: inherit;
  font-size: 10.5px;
  line-height: 1.1;
  white-space: nowrap;
}

.action-state span {
  overflow: hidden;
  color: #8f98aa;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-state.state-ok {
  border-color: rgba(27, 201, 142, 0.36);
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
}

.action-state.state-warn {
  border-color: rgba(224, 192, 126, 0.36);
  background: rgba(224, 192, 126, 0.1);
  color: #e0c07e;
}

.action-state.state-bad {
  border-color: rgba(232, 79, 106, 0.42);
  background: rgba(232, 79, 106, 0.12);
  color: #f0a1af;
}

.action-state.state-info {
  border-color: rgba(25, 151, 198, 0.34);
  background: rgba(25, 151, 198, 0.1);
  color: #7bccee;
}

.top-issues {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  max-width: 320px;
}

.issue-chip {
  padding: 2px 8px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 6px;
  background: rgba(58, 63, 77, 0.3);
  font-size: 11px;
}

.f-actions {
  min-width: 310px;
  white-space: normal;
  text-align: right;
}

.row-btn {
  margin-left: 6px;
  margin-bottom: 6px;
  height: 27px;
  padding: 0 11px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(20, 25, 35, 0.7);
  color: #cfd2da;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 700;
}

.row-btn:hover:not(:disabled) {
  border-color: rgba(25, 151, 198, 0.7);
  color: #eaf7fc;
}

.row-btn.review {
  border-color: rgba(25, 151, 198, 0.38);
  background: rgba(25, 151, 198, 0.08);
  color: #7bccee;
}

.row-btn.apply {
  border-color: rgba(27, 201, 142, 0.4);
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
}

.row-btn.price-retry {
  border-color: rgba(240, 170, 64, 0.42);
  background: rgba(240, 170, 64, 0.08);
  color: #efc071;
}

.row-btn.price-retry:hover:not(:disabled) {
  border-color: rgba(240, 170, 64, 0.76);
  background: rgba(240, 170, 64, 0.14);
  color: #ffe0a8;
}

.row-btn.archive {
  border-color: rgba(224, 192, 126, 0.38);
  background: rgba(224, 192, 126, 0.08);
  color: #d9bd82;
}

.row-btn.archive:hover:not(:disabled) {
  border-color: rgba(224, 192, 126, 0.7);
  background: rgba(224, 192, 126, 0.14);
  color: #f2dba5;
}

.row-btn.ignore {
  color: #9aa3b4;
}

.row-btn.danger {
  border-color: rgba(232, 79, 106, 0.28);
  color: #d694a0;
}

.row-btn.danger:hover:not(:disabled) {
  border-color: rgba(232, 79, 106, 0.62);
  background: rgba(232, 79, 106, 0.1);
  color: #ffd4dc;
}

.row-btn.compact {
  padding: 0 9px;
  color: #9aa3b4;
}

.row-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.status-tag,
.src-tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border: 1px solid rgba(117, 124, 146, 0.3);
  border-radius: 999px;
  color: #9aa3b4;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: capitalize;
}

.count-pill {
  min-width: 26px;
  justify-content: center;
}

.empty {
  padding: 18px;
  color: #6f788b;
  text-align: center;
}

.part-drawer {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.52);
  backdrop-filter: blur(2px);
}

.part-panel {
  width: min(520px, calc(100vw - 28px));
  height: 100%;
  padding: 22px;
  overflow-y: auto;
  border-left: 1px solid rgba(67, 72, 87, 0.9);
  background:
    radial-gradient(circle at 20% 0%, rgba(25, 151, 198, 0.14), transparent 34%),
    rgba(9, 13, 20, 0.96);
  box-shadow: -24px 0 65px rgba(0, 0, 0, 0.42);
}

.finding-panel {
  width: min(680px, calc(100vw - 28px));
}

.review-modal-overlay {
  align-items: flex-start;
  justify-content: center;
  padding: 36px 22px;
  overflow-y: auto;
}

.issue-review-panel {
  width: min(1080px, calc(100vw - 44px));
  height: auto;
  max-height: calc(100vh - 72px);
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 10px;
  box-shadow: 0 28px 90px rgba(0, 0, 0, 0.56);
}

.part-panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.82);
}

.part-panel-head h5 {
  margin: 9px 0 0;
  color: #e4e9f2;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
}

.drawer-type {
  margin-left: 7px;
}

.panel-close {
  width: 32px;
  height: 32px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 7px;
  background: rgba(20, 25, 35, 0.7);
  color: #9aa3b4;
  cursor: pointer;
  font-weight: 800;
}

.panel-close:hover {
  border-color: rgba(25, 151, 198, 0.7);
  color: #dce1eb;
}

.part-panel-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin: 16px 0;
}

.drawer-link-action {
  text-decoration: none;
}

.drawer-link-action:hover {
  text-decoration: none;
}

.panel-loading,
.panel-error {
  margin-bottom: 12px;
  padding: 9px 11px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 800;
}

.panel-loading {
  border: 1px solid rgba(25, 151, 198, 0.34);
  background: rgba(25, 151, 198, 0.08);
  color: #9bdff5;
}

.panel-error {
  border: 1px solid rgba(232, 79, 106, 0.38);
  background: rgba(232, 79, 106, 0.11);
  color: #f0a1af;
}

.part-detail-overview {
  display: grid;
  grid-template-columns: 142px minmax(0, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.part-detail-overview.no-thumb {
  grid-template-columns: 1fr;
}

.part-detail-thumb {
  min-height: 132px;
  border: 1px solid rgba(67, 72, 87, 0.84);
  border-radius: 8px;
  background-color: #e8edf3;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  box-shadow:
    inset 0 0 22px rgba(5, 8, 12, 0.16),
    0 18px 38px rgba(0, 0, 0, 0.22);
}

.part-detail-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.part-detail-fact,
.part-spec-row {
  min-width: 0;
  padding: 9px 10px;
  border: 1px solid rgba(67, 72, 87, 0.78);
  border-radius: 7px;
  background: rgba(14, 18, 27, 0.72);
}

.part-detail-fact span,
.part-spec-row span {
  display: block;
  color: #8f98aa;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.part-detail-fact strong,
.part-spec-row strong,
.part-spec-row a {
  display: block;
  margin-top: 5px;
  overflow-wrap: anywhere;
  color: #dce1eb;
  font-size: 12.5px;
  line-height: 1.35;
}

.part-spec-row a {
  color: #7ed9f5;
}

.part-spec-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 9px;
}

.part-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}

.part-metrics > div {
  min-height: 72px;
  padding: 11px;
  border: 1px solid rgba(67, 72, 87, 0.84);
  border-radius: 8px;
  background: rgba(14, 18, 27, 0.72);
}

.metric-label,
.section-kicker {
  display: block;
  color: #8f98aa;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.part-metrics strong {
  display: block;
  margin-top: 7px;
  color: #eaf0f7;
  font-size: 24px;
  line-height: 1;
}

.metric-bad {
  color: #f0a1af !important;
}

.metric-warn {
  color: #e0c07e !important;
}

.part-panel-section {
  margin-top: 18px;
}

.part-panel-section .top-issues {
  margin-top: 9px;
}

.drawer-findings {
  display: grid;
  gap: 8px;
  margin-top: 9px;
}

.drawer-finding {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 10px;
  padding: 10px;
  border: 1px solid rgba(67, 72, 87, 0.78);
  border-radius: 8px;
  background: rgba(12, 16, 24, 0.7);
}

.drawer-finding-clickable {
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease;
}

.drawer-finding-clickable:hover {
  border-color: rgba(25, 151, 198, 0.46);
  background: rgba(25, 151, 198, 0.06);
}

.drawer-finding p {
  margin: 6px 0 0;
  color: #9aa3b4;
  font-size: 12px;
  line-height: 1.4;
}

.drawer-empty {
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 8px;
}

.drawer-state {
  margin: 14px 0;
}

.drawer-state .action-state {
  max-width: 100%;
}

.issue-review-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(330px, 0.9fr);
  gap: 14px;
  align-items: start;
}

.issue-review-main,
.issue-review-side,
.part-review-card {
  min-width: 0;
}

.issue-review-side {
  display: grid;
  gap: 12px;
}

.part-review-card {
  padding: 12px;
  border: 1px solid rgba(67, 72, 87, 0.84);
  border-radius: 8px;
  background: rgba(10, 14, 22, 0.68);
}

.part-detail-overview.compact {
  grid-template-columns: 104px minmax(0, 1fr);
  margin: 10px 0 0;
}

.part-detail-overview.compact .part-detail-thumb {
  min-height: 104px;
}

.part-detail-overview.compact .part-detail-summary {
  grid-template-columns: 1fr;
}

.finding-summary-grid {
  display: grid;
  grid-template-columns: 1.3fr 1fr 0.8fr;
  gap: 10px;
  margin: 16px 0;
}

.finding-summary-grid > div,
.finding-values > div,
.evidence-card {
  border: 1px solid rgba(67, 72, 87, 0.84);
  border-radius: 8px;
  background: rgba(14, 18, 27, 0.72);
}

.finding-summary-grid > div {
  min-height: 70px;
  padding: 11px;
}

.finding-summary-grid strong {
  display: block;
  margin-top: 7px;
  color: #dce1eb;
  font-size: 13px;
  line-height: 1.35;
}

.finding-values {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.finding-values > div {
  min-height: 112px;
  padding: 12px;
}

.finding-values p {
  margin: 8px 0 0;
  color: #cfd2da;
  font-family: monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.evidence-card {
  margin-top: 9px;
  padding: 12px;
}

.evidence-card > a {
  display: inline-flex;
  margin-bottom: 10px;
  color: #1fb2e8;
  font-weight: 800;
  text-decoration: none;
}

.evidence-row {
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid rgba(47, 51, 61, 0.76);
}

.evidence-row span {
  color: #8f98aa;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.evidence-row strong {
  color: #d0d6e1;
  font-size: 12px;
  line-height: 1.4;
  word-break: break-word;
}

.ai-response-card {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(47, 51, 61, 0.76);
}

.ai-response-row {
  margin-top: 9px;
}

.ai-response-row > span {
  display: block;
  margin-bottom: 5px;
  color: #8f98aa;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.ai-response-row pre {
  max-height: 180px;
  margin: 0;
  padding: 10px;
  overflow: auto;
  border: 1px solid rgba(44, 121, 150, 0.38);
  border-radius: 7px;
  background: rgba(5, 12, 18, 0.82);
  color: #c8d2df;
  font-size: 11px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}

.evidence-raw {
  max-height: 260px;
  margin: 12px 0 0;
  padding: 12px;
  overflow: auto;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 7px;
  background: rgba(7, 10, 16, 0.82);
  color: #aeb5c5;
  font-size: 11px;
  line-height: 1.45;
  white-space: pre-wrap;
}

@media (max-width: 880px) {
  .kpi-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .part-metrics {
    grid-template-columns: 1fr;
  }

  .part-detail-overview,
  .part-detail-summary,
  .part-spec-grid {
    grid-template-columns: 1fr;
  }

  .finding-summary-grid,
  .finding-values,
  .issue-review-grid {
    grid-template-columns: 1fr;
  }

  .review-modal-overlay {
    padding: 16px 10px;
  }

  .issue-review-panel {
    width: min(100%, calc(100vw - 20px));
    max-height: calc(100vh - 32px);
  }
}
</style>
